import { BTN } from "@/lib/btn";
import React, { useEffect, useMemo, useRef, useState } from "react";
import SearchBar from "@/components/SearchBar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { drw } from "../../drawings/api";
import DrawingsTab from "../../drawings/DrawingsTab";
import { useViewState } from "../../drawings/useViewState";
import { openDrawing } from "../../drawings/openDrawing";
import { useFieldShopLinks } from "../../drawings/FieldShopLinks";
import { api } from "../lib/api";
import { useAppSettings } from "../lib/useAppSettings";
import { usePendingEdits } from "../frsedit/pending";
import { activeRows, mixedSummary } from "../lib/mixedDate";
import FrsEditBar from "./FrsEditBar";
import QtyEditCell from "../frsedit/QtyEditCell";
import RoomActionsDialog from "../frsedit/RoomActionsDialog";
import ProductPickerDialog from "../frsedit/ProductPickerDialog";
import BulkEditDialog from "../frsedit/BulkEditDialog";
import AddRoomScopeWizard from "./AddRoomScopeWizard";
import StatusPill from "./StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Send, FileSpreadsheet, X, ChevronRight, ChevronDown, Layers, Building, Compass, DoorClosed, ClipboardCheck, List, LayoutGrid, CalendarDays, RotateCcw, GitPullRequestArrow, Download, Zap, Truck, Maximize2, Minimize2, ClipboardList, Lock, Unlock, History, Pencil, SlidersHorizontal, Undo2, CircleCheck, Link2, ArrowRightToLine, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import FrsMultiUploadDialog from "./FrsMultiUploadDialog";
import { LINEOUT_FIELDS, NoteHistoryField, MaterialsSubSection, DailyTargetField } from "./MobPopover";
import { smartTitleFromItems } from "../lib/mobTitle";
import ReconciliationView from "./ReconciliationView";
import FrsGridView, { rowInstalled, headerLabels } from "./FrsGridView";
import MaterialPhotos from "./MaterialPhotos";
import PhotoGallery from "./PhotoGallery";
import CompletionCard, { completionKey } from "./CompletionCard";
import PlacementCard from "./PlacementCard";
import RoomsMoveDialog from "./RoomsMoveDialog";
import OfciProjectDialog from "./OfciProjectDialog";
import IssueDialog from "./IssueDialog";
import PunchUploadDialog from "./PunchUploadDialog";
import FilterPills from "@/components/FilterPills";
import useTextHighlight from "@/hooks/useTextHighlight";
import PickingTicketDialog from "./PickingTicketDialog";
import ProductRecordDialog from "./ProductRecordDialog";
import PickingRequestDialog from "./PickingRequestDialog";
import PunchView from "./PunchView";
import { useAuth } from "@/lib/AuthContext";
import { fmtDMY } from "./CommitDateInput";
import FrsDateCell from "./FrsDateCell";
import ScopePills from "./ScopePills";
import { ScopeLinkControl, depInto, groupAt, nodeOf, pathLabel, useSchedule, useScheduleSave } from "./ScopeLinks";

// Display label for the Room dimension (filter + tree leaf share this).
// readiness_status values that count as "the GC has handed this room over".
const READY_VALUES = ["ready", "scheduled", "completed"];

export function roomLabelOf(i) {
  const name = (i.room_name || i.room_type || "").trim();
  const num = (i.room_number !== "" && i.room_number != null) ? String(i.room_number) : "";
  if (name && num) return `${num} · ${name}`;
  return num || name || "—";
}

// Lineout note fields that must have at least one entry before the FIRST
// mobilization of a scope can be submitted (per user requirement).
const REQUIRED_LINEOUT_FIELDS = [
  { key: "parking_logistics", label: "Parking and Site Logistics", level: "project" },
];
import { Dialog as RDialog, DialogContent as RDialogContent, DialogHeader as RDialogHeader, DialogTitle as RDialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

// Inclusive workday count between two ISO strings (Mon–Fri only).
function workdaysBetween(startIso, endIso) {
  if (!startIso || !endIso) return 0;
  const s = new Date(startIso + "T00:00:00");
  const e = new Date(endIso + "T00:00:00");
  if (e < s) return 0;
  let n = 0;
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) n += 1;
  }
  return n;
}

const fmtDateLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" });
};

function Select({ value, onChange, options, placeholder, testid }) {
  // Accepts string[] OR {value, label}[] for `options`.
  const norm = (options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <select
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="h-8 text-sm border border-gray-300 rounded-md px-2 bg-white min-w-[120px]"
    >
      <option value="">{placeholder}</option>
      {norm.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

const LEVEL_META = {
  scope:     { icon: Layers,     text: "text-blue-800",   bg: "bg-blue-50",    prefix: "" },
  building:  { icon: Building,   text: "text-indigo-800", bg: "bg-indigo-50",  prefix: "Building " },
  area:      { icon: Compass,    text: "text-cyan-800",   bg: "bg-cyan-50",    prefix: "Area " },
  floor:     { icon: Compass,    text: "text-violet-800", bg: "bg-violet-50",  prefix: "Floor " },
  room:      { icon: DoorClosed, text: "text-gray-800",   bg: "bg-gray-50",    prefix: "" },
};

// Render a hierarchy label with its prefix, guarding against double-prefixing
// if the underlying value already starts with the prefix word (e.g. legacy
// "Building 1" values stored verbatim in FRS Excel). Purely a display helper —
// the underlying frs_items.building / .floor / .area values are unchanged.
function formatLevelLabel(levelType, value) {
  const raw = String(value ?? "").trim();
  if (!raw || raw === "—") return raw || "—";
  const prefix = (LEVEL_META[levelType]?.prefix || "").trim();
  if (!prefix) return raw;
  const p = prefix.toLowerCase();
  return raw.toLowerCase().startsWith(p + " ") || raw.toLowerCase() === p
    ? raw
    : `${prefix} ${raw}`;
}

// Determine which hierarchy levels actually have data (in priority order),
// then build a tree along those levels. Room is always the leaf grouping.
function buildGroups(items) {
  const sortKeys = (keys) =>
    keys.sort((a, b) => {
      const na = parseInt(String(a).replace(/\D+/g, ""), 10);
      const nb = parseInt(String(b).replace(/\D+/g, ""), 10);
      if (!Number.isNaN(na) && !Number.isNaN(nb) && (na !== nb)) return na - nb;
      return String(a).localeCompare(String(b));
    });
  const has = (fn) => items.some((i) => {
    const v = fn(i);
    return v !== undefined && v !== null && String(v).trim() !== "";
  });

  // Build the dynamic level config. Scope is always first; Room always last.
  // Intermediate levels (building / floor / area) only appear if the data
  // actually has values. Room Type is intentionally NOT a tree level — it
  // remains as a filter-only facet (per spec).
  const levels = [{ type: "scope", get: (i) => i.scope_type || "Unspecified" }];
  if (has((i) => i.building))   levels.push({ type: "building", get: (i) => i.building || "—" });
  if (has((i) => i.floor))      levels.push({ type: "floor",    get: (i) => (i.floor !== "" && i.floor != null) ? String(i.floor) : "—" });
  if (has((i) => i.area))       levels.push({ type: "area",     get: (i) => i.area || "—" });
  // Room leaf: combines room_name + room_number when both exist (e.g.
  // "Locker Room · 101"). Falls back to whichever is present.
  levels.push({
    type: "room",
    get: (i) => roomLabelOf(i),
  });

  // Recursively bucket items by the ordered levels.
  const bucket = (its, depth) => {
    if (depth >= levels.length) return null;
    const lvl = levels[depth];
    const map = new Map();
    for (const it of its) {
      const k = lvl.get(it);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(it);
    }
    const nodes = [];
    for (const k of sortKeys([...map.keys()])) {
      const groupItems = map.get(k);
      const isLeaf = depth === levels.length - 1;
      const node = {
        depth,
        levelType: lvl.type,
        key: k,
        path: `${lvl.type}:${k}`,
        items: isLeaf ? groupItems : [],
        children: isLeaf ? [] : (bucket(groupItems, depth + 1) || []),
        allItemIds: groupItems.map((i) => i.id),
      };
      node.totalQty = groupItems.reduce((s, i) => s + (i.quantity || 0), 0);
      node.readyCount = groupItems
        .filter((i) => ["ready", "scheduled", "completed"].includes(i.readiness_status))
        .reduce((s, i) => s + (i.quantity || 0), 0);
      const roomKeys = new Set();
      for (const gi of groupItems) {
        roomKeys.add(`${gi.building || ""}||${gi.floor || ""}||${gi.room_number || ""}||${gi.room_type || ""}`);
      }
      node.roomCount = roomKeys.size;
      // Make path unique by including parent depth/level
      node.path = `${lvl.type}#${depth}:${k}`;
      nodes.push(node);
    }
    return nodes;
  };

  const tree = bucket(items, 0) || [];
  // Make every path globally unique by prefixing parent paths.
  const stamp = (nodes, parentPath) => {
    for (const n of nodes) {
      n.path = `${parentPath}/${n.path}`;
      if (n.children && n.children.length) stamp(n.children, n.path);
    }
  };
  stamp(tree, "");
  collapseSingleChildChains(tree);
  restampDepths(tree, 0);
  return tree;
}

// When a building / floor / area group holds exactly ONE child, that child is
// folded into the parent row instead of costing the user another click:
// "Building 3 › Floor 1 — Mail Room" renders as one row. Applied recursively.
// The parent's `path` (and therefore its expanded-state + note key) is kept so
// existing group notes keep resolving; folded-away paths are remembered in
// `mergedPaths` so a note saved on the old child path still displays.
function collapseSingleChildChains(nodes) {
  const MERGEABLE = ["building", "floor", "area"];
  for (const n of nodes) {
    n.labelChain = n.labelChain || [{ levelType: n.levelType, key: n.key }];
    n.mergedPaths = n.mergedPaths || [];
    while (
      MERGEABLE.includes(n.labelChain[n.labelChain.length - 1].levelType) &&
      n.children.length === 1
    ) {
      const c = n.children[0];
      n.labelChain.push({ levelType: c.levelType, key: c.key });
      n.mergedPaths.push(c.path, ...(c.mergedPaths || []));
      n.children = c.children;
      n.items = c.items;
    }
    if (n.children.length) collapseSingleChildChains(n.children);
  }
}

function restampDepths(nodes, depth) {
  for (const n of nodes) {
    n.depth = depth;
    if (n.children?.length) restampDepths(n.children, depth + 1);
  }
}

// Flatten visible rows according to expanded set. Each row is either
// {kind:'group', node}, {kind:'item', item, depth}, or {kind:'summary', node}.
// Summary rows appear as the LAST child of an expanded Building / Floor /
// Area group and surface a roll-up of unit descriptions and quantities.
function flattenVisible(groups, expanded) {
  const rows = [];
  const visit = (nodes) => {
    for (const n of nodes) {
      rows.push({ kind: "group", node: n });
      if (!expanded.has(n.path)) continue;
      if (n.children.length > 0) {
        visit(n.children);
      } else if (n.items.length > 0) {
        for (const it of n.items) rows.push({ kind: "item", item: it, depth: n.depth + 1 });
      }
      // Append summary row at the end of expanded Building / Floor / Area
      // groups only (NOT room, NOT scope). Non-interactive; quantities here
      // do not feed any totals — just informational.
      if (["building", "floor", "area"].includes(n.levelType)) {
        rows.push({ kind: "summary", node: n });
      }
    }
  };
  visit(groups);
  return rows;
}

// Inline-editable Notes cell. Shows the elevation_sheet as a small amber
// prefix (since the dedicated Elev column was removed) and the user-editable
// `room_notes` text below. Click anywhere to enter edit mode; saves on blur
// or Enter.
function NoteCell({ item, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.room_notes || "");
  React.useEffect(() => { setDraft(item.room_notes || ""); }, [item.room_notes]);
  const save = async () => {
    const next = (draft || "").trim();
    if (next === (item.room_notes || "")) { setEditing(false); return; }
    try {
      await api.patch(`/frs-items/${item.id}`, { room_notes: next });
      onSaved && onSaved(next);
      setEditing(false);
      toast.success("Note saved");
    } catch { toast.error("Save failed"); setEditing(false); }
  };
  if (editing) {
    return (
      <textarea
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { save(); }
          else if (e.key === "Escape") { setDraft(item.room_notes || ""); setEditing(false); }
        }}
        className="w-full text-xs border border-blue-300 rounded px-1.5 py-1 focus:outline-none focus:border-blue-500 min-h-[44px]"
        placeholder="Note…"
        data-testid={`frs-note-edit-${item.id}`}
      />
    );
  }
  return (
    <div
      onClick={() => setEditing(true)}
      className="cursor-text hover:bg-blue-50/40 rounded px-1.5 py-1 -mx-1.5 -my-1 min-h-[20px]"
      data-testid={`frs-note-${item.id}`}
      title="Click to edit"
    >
      {item.elevation_sheet && (
        <span className="inline-flex items-center text-[10px] font-mono text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 mr-1">
          {item.elevation_sheet}
        </span>
      )}
      {item.room_notes ? (
        <span className="text-gray-600">{item.room_notes}</span>
      ) : (
        <span className="text-gray-300 italic">+ note</span>
      )}
    </div>
  );
}

// Inline-editable note cell for GROUP rows (Scope / Building / Floor / Area /
// Room). Persists via the project-level `notes` collection with
// key = `frs:<node.path>` so each unique tree path keeps its own note. Click
// to edit; saves on blur or Enter. Notes are passed in from the parent so we
// don't refetch on every hover.
function GroupNoteCell({ node, projectId, notesByKey, currentUser, onSaved }) {
  const noteKey = `frs:${node.path}`;
  // Folded-away child paths (single-child collapse) can still hold a note that
  // was saved before the fold — fall back to the first one we find.
  const existing = notesByKey[noteKey]
    || (node.mergedPaths || []).map((p) => notesByKey[`frs:${p}`]).find(Boolean)
    || null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(existing?.text || "");
  React.useEffect(() => { setDraft(existing?.text || ""); }, [existing?.id, existing?.text]);
  const save = async () => {
    const next = (draft || "").trim();
    if (next === (existing?.text || "")) { setEditing(false); return; }
    try {
      let saved;
      if (existing?.id) {
        if (next === "") {
          await api.delete(`/notes/${existing.id}`);
          saved = null;
        } else {
          const r = await api.patch(`/notes/${existing.id}`, { text: next });
          saved = r.data;
        }
      } else if (next !== "") {
        const r = await api.post("/notes", {
          project_id: projectId,
          scope_type: "",
          key: noteKey,
          text: next,
          author: currentUser || "",
        });
        saved = r.data;
      } else {
        setEditing(false);
        return;
      }
      onSaved && onSaved(noteKey, saved);
      setEditing(false);
      toast.success(saved ? "Note saved" : "Note cleared");
    } catch { toast.error("Save failed"); setEditing(false); }
  };
  if (editing) {
    return (
      <textarea
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { save(); }
          else if (e.key === "Escape") { setDraft(existing?.text || ""); setEditing(false); }
        }}
        className="w-full text-xs border border-blue-300 rounded px-1.5 py-1 focus:outline-none focus:border-blue-500 min-h-[44px]"
        placeholder="Note…"
        data-testid={`tree-note-edit-${node.path}`}
      />
    );
  }
  return (
    <div
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="cursor-text hover:bg-blue-50/40 rounded px-1.5 py-1 -mx-1.5 -my-1 min-h-[20px] text-xs"
      data-testid={`tree-note-${node.path}`}
      title="Click to edit"
    >
      {existing?.text ? (
        <span className="text-gray-600 whitespace-pre-wrap">{existing.text}</span>
      ) : (
        <span className="text-gray-300 italic">+ note</span>
      )}
    </div>
  );
}

export default function FrsTable({ projectId, installMode = false, installerName = "",
                                  // Jobs › FRS: the same table for the office — no field tooling, no dates.
                                  opsMode = false,
                                  focusItemIds = null, focusLabel = "",
                                  // FT-2 §2 — arriving from a Dispatch Card: project + building +
                                  // floor + area come in pre-applied, one tap clears them, and the
                                  // dropdowns stay editable.
                                  dispatchFilter = null, onClearDispatch = null,
                                  // FLD-3 — Delivery is this same view fed by one ticket's records.
                                  delivery = null,
                                  // PLAN-COMPLETE §5 — marks tapped on a drawing open THIS matrix,
                                  // filtered by `focusItemIds`. `pendingItems` is item_id → units
                                  // installed once the pending change is saved (the grid renders
                                  // the cell from it), `presetFor` hands the real completion dialog
                                  // the same figures, and `onPlanSaved` reports the saved cell back
                                  // so its marks leave the selection.
                                  pendingItems = null, presetFor = null, onPlanSaved = null, autoExpand = false,
                                  // RA note — "Request Mob → Update lineout? Yes" lands here with the scope pre-selected
                                  autoRequestScope = "", onAutoRequestOpened = null }) {
  const { user } = useAuth();
  // Who did it — the signed-in person, never the role. Notes, date changes and
  // picking tickets are all attributed to a name, so the digest and the audit
  // trail can reach them. Defined early so it is available in the many hooks
  // and helpers below (avoids a temporal-dead-zone error).
  const currentUser = user?.email || user?.name || "";
  // FT-2 §6 — placement photos are a Settings > Field toggle and ship OFF.
  const appSettings = useAppSettings();
  const placementPhotosOn = appSettings.material_placement_photos === true;
  // FT-3a — edit the FRS in this grid. Edits are pending until Save; a version can be opened
  // read-only from the history drawer.
  const [editMode, setEditMode] = useState(false);
  const [viewing, setViewing] = useState(null);      // { id, label, mode: view|compare }
  const [viewData, setViewData] = useState(null);
  const pending = usePendingEdits(projectId, editMode);
  // FT-3a parity — the tools the separate editor used to own, now staged like every other edit.
  const [roomActions, setRoomActions] = useState(null);   // the grid room row being acted on
  const [pickerOpen, setPickerOpen] = useState(false);
  const [addRoomOpen, setAddRoomOpen] = useState(false);
  // FT-3 — group the grid by location or by room type (remembered), and the bulk-edit target.
  const [groupBy, setGroupBy] = useState(() => ["room_type", "scope"].includes(localStorage.getItem("frs.groupBy")) ? localStorage.getItem("frs.groupBy") : "location");
  const [bulkTarget, setBulkTarget] = useState(null);   // { label, rooms, col? }
  const [fieldOptions, setFieldOptions] = useState({});
  const [ownItems, setOwnItems] = useState([]);
  // ONE source of truth for header and hierarchy data: every view — FRS, Install → Material and
  // Delivery — reads the project's FRS records from `/frs-items`. Delivery only SCOPES that same
  // set down to the room-product records on its ticket; it never keeps a copy, so an RA header
  // change reaches it with no sync step.
  const ticketScoped = useMemo(() => (delivery
    ? ownItems.filter((it) => delivery.keys?.has(it.frs_item_key))
    : ownItems), [ownItems, delivery?.keys]);   // eslint-disable-line react-hooks/exhaustive-deps
  const allItems = useMemo(() => (viewData?.items?.length ? viewData.items : ticketScoped),
                           [viewData, ticketScoped]);
  const setAllItems = setOwnItems;
  const changedKeys = useMemo(() => new Set([...(viewData?.changed_item_keys || []),
                                             ...(viewData?.added_item_keys || []),
                                             ...(viewData?.removed_item_keys || [])]),
                              [viewData]);
  const [facets, setFacets] = useState({ buildings: [], floors: [], room_types: [], scope_types: [], areas: [] });
  const [filters, setFilters] = useState({
    building: [], area: [], floor: [], room: [], room_type: [], scope_type: [],
    readiness_status: [], mobilization_status: [], search: "", change_orders_only: false,
    short_only: false,
  });
  const [ownSelected, setOwnSelected] = useState(new Set());
  const selected = delivery?.selected || ownSelected;
  const setSelected = delivery?.setSelected || setOwnSelected;
  const [expanded, setExpanded] = useState(new Set()); // default: all collapsed
  // Independent expanded-set for the per-group "Summary" row (one per
  // building/floor/area). Keys are `${node.path}/__summary__`.
  const [summaryExpanded, setSummaryExpanded] = useState(new Set());
  // Keyed map of group-row notes: `frs:<node.path>` → Note object.
  // Loaded once per project; updated in place when GroupNoteCell saves.
  const [groupNotesByKey, setGroupNotesByKey] = useState({});
  // Project-level + scope-level notes used by both the FRS group rows AND
  // the request-dialog Lineout sections. One source of truth — saving in
  // the request dialog updates the popover (and vice versa).
  const [projectNotes, setProjectNotes] = useState([]);
  // All mobilizations on the project — used to detect whether this is the
  // FIRST mob for a given scope (validation gate for required Lineout notes).
  const [projectMobs, setProjectMobs] = useState([]);
  // Unsaved lineout drafts typed in the request dialog (key → text). A typed
  // draft satisfies the 1st-mob validation and is auto-saved on submit, so
  // foremen aren't blocked just because they didn't click "Add Note".
  const [lineoutDrafts, setLineoutDrafts] = useState({});
  const [scopeCodes, setScopeCodes] = useState([]);
  const [viewMode, setViewMode] = useState(
    () => (installMode ? "grid"
      : localStorage.getItem("fieldops-frs-default-view") === "tree" ? "tree" : "grid"),
  ); // 'tree' | 'grid' — seeded from the user's Default FRS view preference
  // P2-4 — progress by Units or Hours (RA defaults Hours, Install Units)
  const [progressMode] = useState("units");         // % complete is units only (the Units / Hours switch was retired)
  const [hoursByRoom, setHoursByRoom] = useState(null);
  useEffect(() => {
    if (installMode || progressMode !== "hours" || !projectId) return;
    api.get(`/projects/${projectId}/hours-by-room`).then((r) => setHoursByRoom(r.data?.rooms || {})).catch(() => setHoursByRoom({}));
  }, [installMode, progressMode, projectId, allItems]);
  const [punchRoom, setPunchRoom] = useState(null);   // P2-5 — PL badge opens that room's punch list
  const [reqOpen, setReqOpen] = useState(false);
  const [installers, setInstallers] = useState([]);
  const [project, setProject] = useState(null);
  // FT-3a — past Open, a scope change needs a reason and raises the review flag. Asked once per
  // Save, not per cell.
  const postOpen = ["open", "completed", "closed"].includes(project?.ops_status || "");
  const [mobForm, setMobForm] = useState({
    title: "",
    requested_start_date: "",
    requested_end_date: "",
    estimated_duration_hours: 8,
    foreman_notes: "",
    priority: "normal",
    num_installers: 1,
    preferred_installer_ids: [],
    badged_required: false,
    drug_test_required: false,
    site_familiarity_required: false,
    is_punchlist: false,
    foreman_preference_notes: "",
  });
  const [latestRecon, setLatestRecon] = useState(null);
  const [reconOpen, setReconOpen] = useState(false);
  const [multiOpen, setMultiOpen] = useState(false);
  const [autoScanning, setAutoScanning] = useState(false);
  const [completions, setCompletions] = useState({});      // `${roomKey}||${scope}` -> completion
  const [roomColumns, setRoomColumns] = useState([]);      // admin-defined room columns
  // Which product field the matrix headers show. Stored on the FRS, so the RA
  // grid and the installer view always agree.
  const [headerConfig, setHeaderConfig] = useState({ header_field: "auto", overrides: {} });
  // Product data corrected straight from the grid's product detail sheet.
  const [editProduct, setEditProduct] = useState(null);
  // FLD-1 — picking mode: the same grid, reconfigured for requesting material.
  const [picking, setPicking] = useState(false);
  const [onHandByUid, setOnHandByUid] = useState({});
  const [coverage, setCoverage] = useState({});      // frs_item_key -> uncovered qty
  // FLD-2 — what the picking sheet colours itself with: requested / on site / on hand
  const [cellState, setCellState] = useState({ lines: {}, on_hand: {} });
  const [pickSel, setPickSel] = useState({});        // frs_item_key -> qty
  const [requestOpen, setRequestOpen] = useState(null);
  const navigate = useNavigate();
  const [completionCell, setCompletionCell] = useState(null); // { room, scope, items }
  // FLD-4 — Install | Material. The same grid and the SAME filter / selection /
  // expand state, so toggling resets nothing.
  const [materialTab, setMaterialTab] = useState(!!delivery);   // Delivery IS the material view
  const [ownOnSite, setOwnOnSite] = useState({});             // frs_item_key -> on-site figures
  const onSite = ownOnSite;
  const setOnSite = setOwnOnSite;
  const [placementRoom, setPlacementRoom] = useState(null);   // { room, items }
  const [ofciProject, setOfciProject] = useState(false);      // FLD-12 — project-wide OFCI verification
  const [issueCell, setIssueCell] = useState(null);           // { room, scope }
  const [issues, setIssues] = useState([]);
  const [reqScope, setReqScope] = useState("");
  // Cluster 9 — opened from a dispatch: show ONLY the dispatched rows until the
  // installer asks for everything. Resets to focused on every open.
  const [focusOn, setFocusOn] = useState(true);
  const [gridExpandSignal, setGridExpandSignal] = useState(0);
  const [gridCollapsed, setGridCollapsed] = useState(null);   // DRW-LINKS — remembered open/closed branches
  // Cluster 10 — view controls (per-user, remembered in this browser).
  const [showCompleted, setShowCompleted] = useState(
    () => !!delivery || localStorage.getItem("fieldops-frs-show-completed") !== "0",
  );
  // FLD-MOB §1 — hiding the navigation is EXPANDED behaviour, never the default. (The phone used
  // to open the field view full screen to reach the far-right LOCATION column; portrait now freezes
  // only ROOM #, so the column is reachable with the navigation still in place.)
  const [expandedView, setExpandedView] = useState(false);
  const [installToolbarSlot, setInstallToolbarSlot] = useState(null);
  // FLD-MOB §3 — in expanded mode on a phone the delivery view keeps only its mode controls, so the
  // workspace has to know when the matrix went full screen.
  const [phoneNarrow, setPhoneNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const read = () => setPhoneNarrow(window.innerWidth < 640);
    window.addEventListener("resize", read);
    window.addEventListener("orientationchange", read);
    return () => { window.removeEventListener("resize", read); window.removeEventListener("orientationchange", read); };
  }, []);   // field view: grid strip renders here
  // Field view keeps the filter block collapsed by default.
  const [fieldFiltersOpen, setFieldFiltersOpen] = useState(false);
  // DRW §8 — filter memory (server-side, per user / project / view) + plan links on top.
  const [urlParams, setUrlParams] = useSearchParams();
  const linkRoom = urlParams.get("room") || "";
  const linkProduct = urlParams.get("product") || "";
  const linkFrom = urlParams.get("from") || "";
  const linkVs = urlParams.get("vs") || "";      // DRW-LINKS — a snapshot to come home to
  const linkScopes = urlParams.get("scopes") || "";   // DRW-B §4 — scopes the drawing was filtered to
  // The Delivery view keeps its OWN remembered filters — a filter left on in Install → Material
  // must never hide material that is on the ticket.
  const viewKey = delivery ? "delivery" : installMode ? (materialTab ? "material" : "install") : picking ? "picking" : "frs";
  const [roomsMove, setRoomsMove] = useState(null);   // FLD-13 bulk room move
  const memory = useViewState(projectId, viewKey);
  const [memoryApplied, setMemoryApplied] = useState(false);
  const [planLinks, setPlanLinks] = useState(null);
  const shopLinks = useFieldShopLinks(projectId);   // DRW-6 — field shop sheets by room (published only)
  const [planOverlay, setPlanOverlay] = useState(null);   // Delivery: the plan over the grid
  // FLD-PHOTO — material photos hang on a physical location; installation photos stay on the
  // completion record. Two galleries, never one.
  const [photoCounts, setPhotoCounts] = useState({ by_location: {}, by_room_location: {}, by_placement: {} });
  const [materialPhotosFor, setMaterialPhotosFor] = useState(null);   // { label, room, frs_item_keys }
  const [installPhotosFor, setInstallPhotosFor] = useState(null);     // { room, scope, completion }
  useEffect(() => {
    if (!projectId) return;
    Promise.all([drw.get(`/projects/${projectId}/sheets`), drw.get(`/projects/${projectId}/spaces`)])
      .then(([sh, sp]) => setPlanLinks({
        sheets: new Set((sh.data.sheets || []).map((x) => String(x.sheet_number || "").replace(/\s+/g, "").toUpperCase())),
        rooms: new Set((sp.data.spaces || []).map((x) => x.frs_room_ref?.room_number).filter(Boolean)),
        hasSet: !!sh.data.current_set,
      }))
      .catch(() => setPlanLinks({ sheets: new Set(), rooms: new Set(), hasSet: false }));
  }, [projectId]);
  // One place that puts a remembered view back on screen — used by the per-view memory and by a
  // Back-from-the-drawing snapshot alike.
  const applyViewState = (st) => {
    if (!st) return;
    if (st.filters) setFilters((f) => ({ ...f, ...st.filters }));
    if (Array.isArray(st.expanded)) setExpanded(new Set(st.expanded));
    if (st.viewMode && !installMode) setViewMode(st.viewMode);
    if (typeof st.showCompleted === "boolean" && !delivery) setShowCompleted(st.showCompleted);
    if (typeof st.materialTab === "boolean" && installMode) setMaterialTab(st.materialTab);
    if (Array.isArray(st.gridCollapsed)) setGridCollapsed(st.gridCollapsed);
  };
  useEffect(() => {
    if (!memory.loaded || memoryApplied) return;
    // arriving back from a drawing: the snapshot owns the view, not the live memory
    if (linkVs) { setMemoryApplied(true); return; }
    applyViewState(memory.remembered);
    setMemoryApplied(true);
    // eslint-disable-next-line
  }, [memory.loaded, memoryApplied, linkVs]);
  // DRW-LINKS — Back from the drawing: filters, expand/collapse, Install/Material toggle and the
  // scroll position come back off the one-shot snapshot, which is then dropped.
  // The one-shot guard is a ref, not state: as a dependency it would tear the effect down again
  // mid-flight and throw the answer away.
  const vsRef = useRef("");
  const [vsDone, setVsDone] = useState(false);     // nothing is written back until it has landed
  useEffect(() => {
    if (!linkVs || vsRef.current === linkVs || !projectId) return;
    // No abort flag here on purpose: React's development double-mount tears the first effect down
    // immediately, and an `alive` guard would throw away the very answer we are waiting for.
    vsRef.current = linkVs;
    drw.get("/view-state", { params: { project_id: projectId, view: linkVs } })
      .then(({ data }) => {
        const st = data.state || {};
        applyViewState(st);
        // the grid has to have laid out its rows first, so this is tried again as they land
        [250, 900, 1800].forEach((ms) => setTimeout(() => writeScroll(st.scroll), ms));
        drw.delete("/view-state", { params: { project_id: projectId, view: linkVs } }).catch(() => {});
      })
      .catch(() => {})
      // the token stays in the URL: dropping it remounts the field shell, which would throw the
      // restored view away. It is a one-shot snapshot — a second read finds nothing.
      .finally(() => setVsDone(true));
    // eslint-disable-next-line
  }, [linkVs, projectId]);
  useEffect(() => {
    if (!memoryApplied || linkRoom) return;
    if (linkVs && !vsDone) return;      // coming back from a drawing: let the snapshot land first
    memory.save({ filters, expanded: [...expanded], viewMode, showCompleted, materialTab,
                  ...(gridCollapsed ? { gridCollapsed } : {}) });
    // eslint-disable-next-line
  }, [filters, expanded, viewMode, showCompleted, materialTab, gridCollapsed, memoryApplied, linkRoom, linkVs, vsDone]);
  // A plan link narrows to its room ON TOP of the remembered filters; nothing is persisted
  // while it is applied, and dropping it restores what was remembered.
  useEffect(() => {
    if (!memoryApplied || !linkScopes) return;
    setFilters((f) => ({ ...f, scope_type: linkScopes.split(",").filter(Boolean) }));
    setUrlParams((cur) => { const n = new URLSearchParams(cur); n.delete("scopes"); return n; }, { replace: true });
    // eslint-disable-next-line
  }, [linkScopes, memoryApplied]);
  const [beforeLink, setBeforeLink] = useState(null);
  useEffect(() => {
    if (!memoryApplied) return;
    if (linkRoom) {
      if (!allItems.length) return;
      memory.suspend(true);
      setBeforeLink((b) => b || { filters, expanded: [...expanded] });
      const labels = [...new Set(allItems.filter((i) => String(i.room_number ?? "") === String(linkRoom)).map(roomLabelOf))];
      setFilters((f) => ({ ...f, building: [], area: [], floor: [], room: labels.length ? labels : [linkRoom], room_type: [],
                           search: linkProduct || "", change_orders_only: false, short_only: false }));
      setExpanded(collectAllPaths());
      setGridExpandSignal((n) => n + 1);
    } else if (beforeLink) {
      setFilters(beforeLink.filters);
      setExpanded(new Set(beforeLink.expanded));
      setBeforeLink(null);
      memory.suspend(false);
    }
    // eslint-disable-next-line
  }, [linkRoom, linkProduct, memoryApplied, allItems.length]);
  const dropLink = () => setUrlParams((cur) => { const n = new URLSearchParams(cur); ["room", "product", "from"].forEach((k) => n.delete(k)); return n; }, { replace: true });
  // Where the view is scrolled to: the window on a phone, and the grid's own scroller on a
  // computer — a drawing link has to come home to both.
  const gridScroller = () => document.querySelector('[data-testid="frs-grid-scroll"]');
  const readScroll = () => {
    const g = gridScroller();
    return { y: Math.round(window.scrollY || 0), gy: Math.round(g?.scrollTop || 0), gx: Math.round(g?.scrollLeft || 0) };
  };
  const writeScroll = (sc) => {
    if (!sc) return;
    if (typeof sc.y === "number") window.scrollTo({ top: sc.y, behavior: "auto" });
    const g = gridScroller();
    if (g) { if (typeof sc.gy === "number") g.scrollTop = sc.gy; if (typeof sc.gx === "number") g.scrollLeft = sc.gx; }
  };
  const openPlan = async ({ sheet = "", room = "", product = "" }) => {
    // Delivery opens the SAME drawings view over the top of the grid, so nothing about the
    // delivery — filters, selection, expanded branches, typed quantities, mode, scroll — is lost.
    if (delivery) { setPlanOverlay({ sheet, room, product }); return; }
    // DRW-LINKS — straight to the Drawings tab, no interstitial. The exact view is snapshotted
    // under a one-shot token first, so Back comes home to it whatever happens in between.
    const token = `origin:${Math.random().toString(36).slice(2, 10)}`;
    try {
      await memory.saveNow({
        filters, expanded: [...expanded], viewMode, showCompleted, materialTab,
        ...(gridCollapsed ? { gridCollapsed } : {}),
        scroll: readScroll(), view: viewKey,
      }, token);
    } catch { /* offline — Back still lands on the view, just without the snapshot */ }
    openDrawing(navigate, { project: projectId, sheet, room, product, from: viewKey, vs: token, scopes: filters.scope_type || [] });
  };
  // Reset is one icon: press to clear + forget; press again (before picking anything new) to undo.
  const [resetUndo, setResetUndo] = useState(null);
  const gridExportRef = useRef(null);
  const resetFilters = async () => {
    setResetUndo({ filters, expanded: new Set(expanded) });
    clearFilters(); setExpanded(new Set()); await memory.reset();
  };
  const undoReset = () => { if (!resetUndo) return; setFilters(resetUndo.filters); setExpanded(resetUndo.expanded); setResetUndo(null); };
  // Cluster 11 — punchlist
  const [punchOpen, setPunchOpen] = useState(false);
  const [punchItems, setPunchItems] = useState([]);
  const focusIds = useMemo(() => new Set(focusItemIds || []), [focusItemIds]);
  const focusActive = focusOn && focusIds.size > 0;
  // FT-2 §2 — the dispatch card's building / floor / area become REAL filter values (matched
  // case-insensitively against what the project actually carries, so a stale label is dropped
  // rather than emptying the grid). Applied once, then the installer owns the dropdowns.
  const [dispatchApplied, setDispatchApplied] = useState(false);
  useEffect(() => {
    if (!dispatchFilter || !memoryApplied || dispatchApplied || !allItems.length) return;
    const pick = (dim, want) => {
      const w = String(want || "").trim().toLowerCase();
      if (!w) return [];
      const hit = [...new Set(allItems.map((it) => DIMS[dim](it)))]
        .find((v) => String(v).trim().toLowerCase() === w);
      return hit ? [hit] : [];
    };
    setFilters((f) => ({ ...f, building: pick("building", dispatchFilter.building),
                         floor: pick("floor", dispatchFilter.floor),
                         area: pick("area", dispatchFilter.area), room: [] }));
    setDispatchApplied(true);
    // eslint-disable-next-line
  }, [dispatchFilter, memoryApplied, dispatchApplied, allItems.length]);
  // History tab → "View / Compare" lands here with the version on the URL.
  useEffect(() => {
    const vid = urlParams.get("frsVersion");
    if (!vid) return;
    setViewing({ id: vid, label: urlParams.get("frsLabel") || "version", mode: urlParams.get("frsMode") || "view" });
    setEditMode(false);
    setUrlParams((cur) => { const n = new URLSearchParams(cur); ["frsVersion", "frsLabel", "frsMode"].forEach((k) => n.delete(k)); return n; }, { replace: true });
    // eslint-disable-next-line
  }, [urlParams]);
  // FT-3a — a version opens read-only; Back to Current drops the snapshot.
  useEffect(() => {
    if (!viewing) { setViewData(null); return undefined; }
    let alive = true;
    api.get(`/frs-grid/version/${viewing.id}`, { params: { project_id: projectId } })
      .then(({ data }) => { if (alive) setViewData(data); })
      .catch(() => { if (alive) { setViewData(null); toast.error("Could not open that version"); } });
    return () => { alive = false; };
  }, [viewing, projectId]);

  // Leaving with pending edits must not lose them silently.
  useEffect(() => {
    if (!pending.count) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [pending.count]);

  const leaveEditMode = () => {
    if (pending.count && !window.confirm(
      `${pending.count} change(s) are not saved yet.\n\nOK keeps them waiting for you; `
      + `Cancel stays in edit mode.`)) return;
    setEditMode(false);
  };

  const roomLabelOf = (r) => [r.room_number && `Room ${r.room_number}`, r.room_name]
    .filter(Boolean).join(" ") || "Room";

  const roomRefOf = (r, scope) => ({
    project_id: projectId, building: r.building || "", floor: String(r.floor ?? ""),
    area: r.area || "", room_number: String(r.room_number ?? ""),
    ...(scope ? { scope_type: scope } : {}),
  });

  const clearDispatch = () => {
    // One tap: the filters go, the row focus goes, and the cleared state is what gets remembered.
    // The URL keeps `?mob=` on purpose — dropping it would remount this component and the
    // remembered view state would put the dispatch filters straight back.
    setFilters((f) => ({ ...f, building: [], floor: [], area: [], room: [] }));
    setFocusOn(false);
    onClearDispatch?.();
  };
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // All rows for the project are fetched once; every filter (including
      // search) is applied client-side so the dropdowns can cascade off the
      // same dataset.
      const r = await api.get("/frs-items", { params: { project_id: projectId } });
      setAllItems(r.data || []);
      if (delivery) { await delivery.reload?.(); await loadOnSite(); }
    } finally { setLoading(false); }
  };
  const loadFacets = async () => {
    const r = await api.get("/frs-items/facets", { params: { project_id: projectId } });
    setFacets(r.data || {});
  };
  const loadRoomColumns = async () => {
    try {
      const r = await api.get("/frs-editor/room-columns", { params: { project_id: projectId } });
      setRoomColumns(r.data || []);
    } catch { setRoomColumns([]); }
  };
  const loadHeaderConfig = async () => {
    try {
      const r = await api.get("/frs-editor/header-config", { params: { project_id: projectId } });
      setHeaderConfig({ header_field: r.data?.header_field || "auto",
                        overrides: r.data?.overrides || {} });
    } catch { setHeaderConfig({ header_field: "auto", overrides: {} }); }
  };
  const saveHeaderConfig = async (patch) => {
    const next = { ...headerConfig, ...patch };
    setHeaderConfig(next);                       // optimistic — it is a display setting
    try {
      const r = await api.post("/frs-editor/header-config", {
        project_id: projectId, actor: currentUser || "", ...patch,
      });
      setHeaderConfig({ header_field: r.data?.header_field || "auto",
                        overrides: r.data?.overrides || {} });
    } catch (e) {
      loadHeaderConfig();
      toast.error(e.response?.data?.detail || "Could not save the header setting");
    }
  };
  const loadMaterial = async () => {
    try {
      const [oh, cov, cells] = await Promise.all([
        api.get("/on-hand", { params: { project_id: projectId } }),
        api.get("/material/coverage", { params: { project_id: projectId } }),
        api.get("/material/cell-status", { params: { project_id: projectId } }),
      ]);
      setOnHandByUid(Object.fromEntries((oh.data || []).map((r) => [r.product_uid, r])));
      setCoverage(Object.fromEntries((cov.data?.rows || []).map((r) => [r.frs_item_key, r.uncovered])));
      setCellState(cells.data || { lines: {}, on_hand: {} });
    } catch { /* picking still works, the indicators just stay grey */ }
  };
  const loadOnSite = async () => {
    try {
      const { data } = await api.get("/material/onsite-grid", { params: { project_id: projectId } });
      setOnSite(data.items || {});
    } catch { /* the Material view just reads empty */ }
  };
  const pickQtyFor = (it) => {
    const un = coverage[it.frs_item_key];
    return un === undefined ? Math.max(Number(it.quantity) || 0, 0) : un;
  };
  const togglePickRows = (rows) => {
    setPickSel((cur) => {
      const next = { ...cur };
      const anyOn = (rows || []).some((it) => next[it.frs_item_key]);
      for (const it of rows || []) {
        if (anyOn) delete next[it.frs_item_key];
        else {
          const q = pickQtyFor(it);
          if (q > 0) next[it.frs_item_key] = q;
        }
      }
      return next;
    });
  };
  const togglePickProduct = (col, rowsFromGrid) => {
    // The grid hands over exactly the rows under that column. Matching on product_uid alone used
    // to select an entire scope, because area-level FRS rows carry no product uid at all.
    const rows = rowsFromGrid?.length ? rowsFromGrid
      : items.filter((it) => (it.product_uid || "") === (col.uid || "")
          && (it.scope_type || "") === (col.scope || ""));
    togglePickRows(rows);
  };
  const pickSelection = useMemo(() => {
    const byKey = new Map(allItems.map((it) => [it.frs_item_key, it]));
    return Object.entries(pickSel).map(([key, qty]) => {
      const it = byKey.get(key) || {};
      // Area-level FRS rows carry no room number and no product uid, so the room and product
      // identity have to fall back the same way the FRS store mints them — otherwise the summary
      // read "2 room(s) · 1 product(s)" for a whole scope.
      return { frs_item_key: key, qty,
               room_key: [it.building || "", it.floor || "", it.area || "",
                          it.room_number || it.room_type || it.room_name || ""].join("|"),
               product_uid: it.product_uid || it.product_short || it.product_part_number
                            || it.product_code || "",
               product_short: it.product_short || "" };
    });
    // eslint-disable-next-line
  }, [pickSel, allItems]);
  const loadLatestRecon = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/reconciliation/latest`);
      setLatestRecon(r.data?.reconciliation ? r.data : null);
    } catch {
      setLatestRecon(null);
    }
  };
  const loadInstallers = async () => {
    try {
      const r = await api.get("/installers");
      // Only role="Installer" qualifies for the Requested Installers picker —
      // Foremen/Leads/Warehouse roles intentionally excluded so foremen request
      // their installers, not leads/managers.
      setInstallers((r.data || []).filter((i) => i.active && (i.role || "") === "Installer"));
    } catch {
      setInstallers([]);
    }
  };
  const loadProject = async () => {
    try {
      const r = await api.get(`/projects/${projectId}`);
      setProject(r.data || null);
    } catch {
      setProject(null);
    }
  };
  // Load all project-level notes; filter the ones whose `key` starts with
  // "frs:" — those are the per-tree-path group notes — into a map for fast
  // lookup keyed by `frs:<path>`.
  const loadGroupNotes = async () => {
    try {
      const r = await api.get("/notes", { params: { project_id: projectId } });
      const map = {};
      for (const n of (r.data || [])) {
        if ((n.key || "").startsWith("frs:")) map[n.key] = n;
      }
      setGroupNotesByKey(map);
      // Hydrate the same response into the full project-notes state for the
      // request-dialog Lineout sections.
      setProjectNotes(r.data || []);
    } catch {
      setGroupNotesByKey({});
      setProjectNotes([]);
    }
  };
  // Pull the project's existing mobilizations so we can detect whether the
  // current request is the FIRST mob for the selected scope (validation gate).
  const loadProjectMobs = async () => {
    try {
      const r = await api.get("/mobilizations", { params: { project_id: projectId } });
      setProjectMobs(r.data || []);
    } catch { setProjectMobs([]); }
  };
  const loadScopeCodes = async () => {
    try {
      const r = await api.get("/scope-codes");
      setScopeCodes(r.data || []);
    } catch { setScopeCodes([]); }
  };
  const loadPhotoCounts = async () => {
    try {
      const { data } = await api.get("/material/photo-counts", { params: { project_id: projectId } });
      setPhotoCounts({ by_location: data.by_location || {}, by_room_location: data.by_room_location || {},
                       by_placement: data.by_placement || {} });
    } catch { setPhotoCounts({ by_location: {}, by_room_location: {}, by_placement: {} }); }
  };

  const loadCompletions = async () => {
    try {
      const { data } = await api.get("/completions", { params: { project_id: projectId } });
      const map = {};
      for (const c of data || []) map[completionKey(c, c.scope_type)] = c;
      setCompletions(map);
    } catch { setCompletions({}); }
  };
  const loadPunch = async () => {
    try {
      const { data } = await api.get("/punch-items", { params: { project_id: projectId } });
      setPunchItems(data || []);
    } catch { setPunchItems([]); }
  };
  const loadIssues = async () => {
    try {
      const { data } = await api.get("/issues", { params: { project_id: projectId } });
      setIssues(data || []);
    } catch { setIssues([]); }
  };
  useEffect(() => { if (projectId) { loadIssues(); loadPunch(); } }, [projectId]);
  useEffect(() => { if (projectId) { load(); loadFacets(); loadLatestRecon(); loadInstallers(); loadProject(); loadGroupNotes(); loadProjectMobs(); loadScopeCodes(); loadCompletions(); loadLevelDates(); loadRoomColumns(); loadHeaderConfig(); loadOnSite(); loadPhotoCounts(); } }, [projectId]);
  // a delivery action (place / move / not delivered) re-reads the real placement map behind LOCATION
  useEffect(() => { if (projectId && delivery) loadOnSite(); }, [delivery?.version]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Hand the scoped FRS records back so the Delivery view can map a picked record to its line.
  useEffect(() => { if (delivery?.onScope) delivery.onScope(allItems); }, [allItems, delivery?.onScope]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Client-side filtering + cascading option lists -------------------
  const DIMS = {
    scope_type: (i) => i.scope_type || "",
    building:   (i) => i.building || "",
    floor:      (i) => (i.floor !== "" && i.floor != null) ? String(i.floor) : "",
    area:       (i) => i.area || "",
    room:       (i) => roomLabelOf(i),
    room_type:  (i) => i.room_type || "",
    readiness_status:    (i) => i.readiness_status || "",
    mobilization_status: (i) => i.mobilization_status || "",
  };
  const searchParsed = useMemo(() => {
    const LEVEL = { building: "building", bldg: "building", floor: "floor", flr: "floor", level: "floor", area: "area", room: "room_number", rm: "room_number" };
    const toks = (filters.search || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
    const levels = []; const words = []; const phrases = [];
    for (let i = 0; i < toks.length; i++) {
      if (LEVEL[toks[i]] && toks[i + 1]) { levels.push({ field: LEVEL[toks[i]], value: toks[i + 1] }); phrases.push(`${toks[i]} ${toks[i + 1]}`, toks[i + 1].length > 1 ? toks[i + 1] : ""); i++; }
      else { words.push(toks[i]); phrases.push(toks[i]); }
    }
    return { levels, words, phrases: phrases.filter(Boolean) };
  }, [filters.search]);
  const matchesFilters = (it, skipDim) => {
    for (const [dim, get] of Object.entries(DIMS)) {
      if (dim === skipDim) continue;
      const sel = filters[dim] || [];
      if (sel.length && !sel.includes(get(it))) return false;
    }
    // Global search: "floor 2" / "building A" / "room 101" / "area B" match that level; any other word
    // must appear somewhere in the row (room, scope, product, notes…)
    if (searchParsed.levels.length || searchParsed.words.length) {
      for (const { field, value } of searchParsed.levels) {
        const v = String(it[field] ?? "").toLowerCase();
        const lab = it[field] ? formatLevelLabel(field === "room_number" ? "room" : field, it[field]).toLowerCase() : "";
        if (v !== value && !lab.endsWith(` ${value}`) && !(field === "room_number" && v.includes(value))) return false;
      }
      if (searchParsed.words.length) {
        const hay = [
          it.building, it.floor, it.area, it.room_number, it.room_name, it.room_type, it.rr_type,
          it.scope_type, it.scope_code, it.product_code, it.product_short, it.product_description, it.room_notes,
          it.plan_sheet, it.elevation_sheet, it.floor_plan, it.readiness_status, it.mobilization_status,
        ].map((x) => String(x ?? "")).join(" \u0001 ").toLowerCase();
        if (!searchParsed.words.every((t) => hay.includes(t))) return false;
      }
    }
    return true;
  };
  // Rooms whose every scope is 100% complete (units-weighted) — hidden unless
  // "Show completed rooms" is ticked.
  const completedRoomKeys = useMemo(() => {
    const agg = new Map();
    for (const it of allItems) {
      const rk = [it.building || "", it.floor || "", it.area || "", it.room_number || "", it.room_type || ""].join("||");
      const units = Number(it.quantity || 0);
      if (units <= 0) continue;
      const cur = agg.get(rk) || { units: 0, done: 0 };
      cur.units += units;
      cur.done += rowInstalled(it, completions[`${rk}||${it.scope_type || ""}`]);
      agg.set(rk, cur);
    }
    const done = new Set();
    for (const [rk, v] of agg.entries()) if (v.units > 0 && v.done / v.units >= 1) done.add(rk);
    return done;
  }, [allItems, completions]);

  const punchCounts = useMemo(() => {
    const m = {};
    const byId = new Map(allItems.map((it) => [it.id, it]));
    for (const p of punchItems) {
      if (p.status === "closed_by_gc" || p.status === "complete") continue;
      const at = (!p.room_number && byId.get(p.frs_item_id)) || p;
      const rk = [at.building || "", at.floor || "", at.area || "", at.room_number || "", at.room_type || ""].join("||");
      m[rk] = (m[rk] || 0) + 1;
    }
    return m;
  }, [punchItems, allItems]);

  const items = useMemo(
    () => allItems.filter((it) => matchesFilters(it, null))
      .filter((it) => !focusActive || focusIds.has(it.id))
      .filter((it) => {
        // P2-5 — a finished room comes back while it has open punch
        const rk = [it.building || "", it.floor || "", it.area || "", it.room_number || "", it.room_type || ""].join("||");
        return showCompleted || !completedRoomKeys.has(rk) || punchCounts[rk] > 0;
      }),
    [allItems, JSON.stringify(filters), focusActive, focusIds, showCompleted, completedRoomKeys, punchCounts],
  );
  // P2-1 — staged quantities show in the grid (cells, row and grand totals) before Save.
  const liveItems = useMemo(() => {
    if (!editMode || !pending.count) return items;
    return items.map((it) => {
      const st = pending.get(`q:${it.id}`);
      return st && !it.is_delta ? { ...it, quantity: st.op.qty, _saved_qty: it.quantity } : it;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, editMode, pending.edits]);

  // Rooms that matched the filters but were hidden only because they are 100% complete —
  // so a scope whose every room is finished (e.g. TC) explains itself instead of going blank.
  const hiddenCompleteRooms = useMemo(() => {
    if (showCompleted || items.length) return 0;
    const keys = new Set();
    for (const it of allItems) {
      if (!matchesFilters(it, null) || (focusActive && !focusIds.has(it.id))) continue;
      const rk = [it.building || "", it.floor || "", it.area || "", it.room_number || "", it.room_type || ""].join("||");
      if (completedRoomKeys.has(rk)) keys.add(rk);
    }
    return keys.size;
  }, [allItems, items, JSON.stringify(filters), focusActive, focusIds, showCompleted, completedRoomKeys]);
  const emptyState = items.length === 0 && hiddenCompleteRooms > 0 ? (
    <div className="px-6 py-10 text-center text-sm text-gray-600 dark:text-gray-300" data-testid="frs-empty-complete">
      <CircleCheck className="mx-auto mb-2 h-6 w-6 text-emerald-500" strokeWidth={1.5} />
      All {hiddenCompleteRooms} matching room{hiddenCompleteRooms === 1 ? " is" : "s are"} 100% complete and hidden.
      <button type="button" onClick={() => setShowCompleted(true)} data-testid="frs-empty-show-completed"
              className="ml-2 font-semibold text-[#007BFF] hover:underline">Show completed rooms</button>
    </div>
  ) : null;

  // FT-3a — the rooms in the current view and the scopes/products each one carries, so the
  // product picker and the room dialog work from what is actually on screen.
  const editScopes = useMemo(
    () => [...new Set(allItems.map((i) => i.scope_type).filter((s) => s && s !== "PUNCH"))].sort(),
    [allItems]);
  const editRooms = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const key = [it.building || "", it.floor || "", it.area || "", it.room_number || "",
                   it.room_type || ""].join("||");
      if (!m.has(key)) {
        m.set(key, { key, label: roomLabelOf(it), ref: roomRefOf(it), scopes: [], uids: [] });
      }
      const r = m.get(key);
      if (it.scope_type && !r.scopes.includes(it.scope_type)) r.scopes.push(it.scope_type);
      const uid = it.product_uid || "";
      if (uid && !r.uids.includes(uid)) r.uids.push(uid);
    }
    return [...m.values()];
  }, [items]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Room-information suggestions for the Add Room wizard — loaded only when edit mode opens it.
  useEffect(() => {
    if (!addRoomOpen || !projectId || Object.keys(fieldOptions).length) return;
    api.get("/frs-editor/room-field-options", { params: { project_id: projectId } })
      .then(({ data }) => setFieldOptions(data || {}))
      .catch(() => setFieldOptions({}));
  }, [addRoomOpen, projectId]);   // eslint-disable-line react-hooks/exhaustive-deps

  // FLD-13 — the rooms behind the ticked rows; the backend room key is building|floor|room number.
  const selectedRooms = useMemo(() => {
    const m = new Map();
    items.forEach((it) => {
      if (!selected.has(it.id)) return;
      const key = [it.building, it.floor, it.room_number].map((x) => String(x || "").trim().toLowerCase()).join("|");
      if (!m.has(key)) m.set(key, { room_key: key, label: `${it.room_number || "?"}${it.room_name || it.room_type ? ` · ${it.room_name || it.room_type}` : ""}` });
    });
    return [...m.values()];
  }, [items, selected]);
  // Options for one dropdown = distinct values present in the rows that match
  // every OTHER active filter → cascades in both directions.
  const optionsFor = (dim) => {
    const get = DIMS[dim];
    const vals = new Set();
    for (const it of allItems) {
      if (!matchesFilters(it, dim)) continue;
      const v = get(it);
      if (v !== "" && v != null) vals.add(v);
    }
    return [...vals].sort((a, b) => {
      const na = parseInt(String(a).replace(/\D+/g, ""), 10);
      const nb = parseInt(String(b).replace(/\D+/g, ""), 10);
      if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb;
      return String(a).localeCompare(String(b));
    });
  };
  const setFilterDim = (dim, vals) => setFilters((f) => ({ ...f, [dim]: vals }));

  // ---- Cluster 5: schedule dates -----------------------------------------
  // The backend resolves each row's effective dates (manual > schedule
  // mapping > project level) and returns them on every FRS item; the UI only
  // aggregates them per room+scope and writes changes back.
  const [shiftPrompt, setShiftPrompt] = useState(null); // { field, days, origin, label }
  const roomDates = (its) => {
    const list = (its || []).filter(Boolean);
    if (list.length === 0) return null;
    const first = list[0];
    // A Grid row can hold several scopes; dates belong to room+scope, so a
    // mixed row shows the envelope (earliest start / latest ABS / earliest GC)
    // read-only and asks the user to filter to one scope to edit.
    const scopes = [...new Set(list.map((i) => i.scope_type || ""))];
    if (scopes.length > 1) {
      const nz = (arr) => arr.filter(Boolean).sort();
      const starts = nz(list.map((i) => i.eff_start));
      const abss = nz(list.map((i) => i.eff_abs));
      const gcs = nz(list.map((i) => i.eff_gc));
      const needs = nz(list.map((i) => i.eff_need));
      return {
        need: needs[0] || "", needSource: "multi",
        start: starts[0] || "", startSource: "multi",
        abs: abss[abss.length - 1] || "", absSource: "multi",
        gc: gcs[0] || "", gcSource: "multi",
        conflict: list.some((i) => i.date_conflict),
        duration: 0, multiScope: true, origin: null,
      };
    }
    return {
      need: first.eff_need || "",
      needSource: first.eff_need_source || "",
      start: first.eff_start || "",
      startSource: first.eff_start_source || "",
      abs: first.eff_abs || "",
      absSource: first.eff_abs_source || "",
      gc: first.eff_gc || "",
      gcSource: first.eff_gc_source || "",
      conflict: !!first.date_conflict,
      duration: first.duration_days_per_room || 0,
      // completion lock (explicit + reversible): the server decides, the row shows it
      roomComplete: !!first.room_complete,
      frozen: !!first.dates_frozen,
      unlocked: first.dates_unlocked || null,
      mixed: list.some((i) => (i.eff_start || "") !== (first.eff_start || "")
        || (i.eff_abs || "") !== (first.eff_abs || "")),
      origin: {
        scope_type: first.scope_type || "", building: first.building || "",
        floor: first.floor || "", area: first.area || "",
        room_number: first.room_number || "", room_type: first.room_type || "",
      },
    };
  };
  const roomConflict = (its) => (its || []).some((i) => i.date_conflict);
  const dayDiff = (a, b) => {
    if (!a || !b) return 0;
    return Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / 86400000);
  };
  // Explicit, reversible completion date lock (who/when kept on the rows + audit log).
  // RA note (2026-10): the "100% · unlock dates" pill left the grid; the endpoint stays for the audit trail.

  const saveDate = async (origin, field, value) => {
    await api.post("/frs-dates/set", { project_id: projectId, ...origin, field, value });
  };
  // Room dates go through the same schedule engine as every level above: same-as-parent stays
  // inherited, a different date locks the room, linked scopes and dependencies follow.
  const onDateChange = async (origin, field, prevValue, value, label) => {
    if (field === "need") {
      try { await saveDate(origin, field, value); await load(); toast.success("Date saved"); }
      catch (e) { toast.error(e.response?.data?.detail || "Could not save date"); }
      return;
    }
    await scheduleSave({ level: "room", building: origin.building || "", floor: String(origin.floor ?? ""), area: origin.area || "",
      room_number: origin.room_number || "", room_type: origin.room_type || "", scope_type: origin.scope_type || "" }, field, value);
  };
  const runShift = async (mode) => {
    const { field, days, origin } = shiftPrompt;
    setShiftPrompt(null);
    if (mode === "area") { toast.success("This area only — no other rooms moved"); return; }
    try {
      const { data } = await api.post("/frs-dates/shift", {
        project_id: projectId, ...origin, field, days, mode, by: currentUser || "",
      });
      toast.success(`${data.rooms_affected} room${data.rooms_affected === 1 ? "" : "s"} shifted ${days > 0 ? "+" : ""}${days} days`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Shift failed"); }
  };

  // ---- Cluster 13: dates at any level, with locks ------------------------
  const [pickingOpen, setPickingOpen] = useState(false);
  const [levelDates, setLevelDates] = useState([]);
  const { rel: scheduleRel, reload: reloadSchedule } = useSchedule(projectId);
  const [datesCollapsed, setDatesCollapsed] = useState(true);   // Completion column hidden on every load
  const toggleDates = () => setDatesCollapsed((v) => !v);
  const onlyStart = datesCollapsed && !picking;
  const [linkPrompt, setLinkPrompt] = useState("");   // level key that just got a date → offer to link
  const scopesAtNode = (spec) => {
    const codes = new Map();
    for (const i of allItems || []) {   // every scope at this node, not just the filtered one
      if (i.is_delta || i.is_punch) continue;
      if (spec.level !== "project" && String(i.building || "") !== String(spec.building || "")) continue;
      if (["floor", "area"].includes(spec.level) && String(i.floor ?? "") !== String(spec.floor ?? "")) continue;
      if (spec.level === "area" && String(i.area || "") !== String(spec.area || "")) continue;
      const t = i.scope_type || "";
      if (t && t !== spec.scope_type) codes.set(t, i.scope_code || t);
    }
    return [...codes.entries()].map(([type, code]) => ({ code, type }));
  };
  const loadLevelDates = async () => {
    try {
      const { data } = await api.get("/frs-dates/levels", { params: { project_id: projectId } });
      setLevelDates(data || []);
    } catch { setLevelDates([]); }
  };
  // An explicit entry IS the lock. The deepest level holding one wins; clearing
  // it re-inherits from the level above.
  const lockedEntry = (spec, field) => (levelDates || []).find((e) =>
    e.level === spec.level && e.field === field &&
    (e.scope_type || "") === (spec.scope_type || "") &&
    String(e.building || "") === String(spec.building || "") &&
    String(e.floor || "") === String(spec.floor || "") &&
    String(e.area || "") === String(spec.area || ""));

  // Older FRS rows carry no product_uid. The products list backfills uids server-side, so
  // resolve the identity there (scope + code + description) instead of refusing the edit.
  const openProductEdit = async (info) => {
    if (info.product_uid) { setEditProduct(info); return; }
    try {
      const { data } = await api.get("/frs-editor/products", { params: { project_id: projectId, scope_type: info.scope_type } });
      const list = data?.products || data || [];
      const norm = (v) => String(v || "").trim().toLowerCase();
      const hit = list.find((x) => norm(x.product_code) === norm(info.product_code) && norm(x.product_description) === norm(info.product_description))
        || list.find((x) => norm(x.product_code) === norm(info.product_code) && (!info.product_description || norm(x.product_description).startsWith(norm(info.product_description).slice(0, 24))))
        || (list.filter((x) => norm(x.product_code) === norm(info.product_code)).length === 1 ? list.find((x) => norm(x.product_code) === norm(info.product_code)) : null);
      if (!hit?.product_uid) { toast.error("Could not match this product to its FRS record"); return; }
      setEditProduct({ ...info, product_uid: hit.product_uid });
      load();   // rows now carry the backfilled uid
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load this product"); }
  };
  const { save: scheduleSave, dialogs: scheduleDialogs } = useScheduleSave({ projectId, actor: currentUser || "", view: "frs", rel: scheduleRel,
    onChanged: () => Promise.all([loadLevelDates(), load(), reloadSchedule()]) });
  const saveLevelDate = async (spec, field, value) => {
    const res = await scheduleSave({ level: spec.level, building: spec.building || "", floor: String(spec.floor || ""), area: spec.area || "",
      scope_type: spec.scope_type || "" }, field, value);
    const already = groupAt(scheduleRel, nodeOf({ level: spec.level, building: spec.building || "", floor: String(spec.floor || ""), area: spec.area || "", scope_type: spec.scope_type || "" }));
    if (res?.applied && value && !already && scopesAtNode(spec).length >= (spec.scope_type ? 1 : 2)) setLinkPrompt(`${spec.level}|${spec.scope_type}|${spec.building || ""}|${spec.floor || ""}|${spec.area || ""}`);
  };

  // Does a level entry cover this FRS row? (mirror of the backend rule)
  const LEVEL_RANK = { project: 0, building: 1, floor: 2, area: 3, room: 4 };
  const entryCovers = (e, it) => {
    if ((e.scope_type || "") && (e.scope_type || "") !== (it.scope_type || "")) return false;
    const same = (k) => String(e[k] || "") === String(it[k] || "");
    const lvl = e.level || "project";
    if (lvl === "project") return true;
    if (lvl === "building") return same("building");
    if (lvl === "floor") return same("building") && same("floor");
    return same("building") && same("floor") && same("area");
  };
  // Locks that sit at or below this header and would out-rank a date set here — the reason a
  // parent date "does not take" on some rooms. Room-level manual dates count too.
  const locksBelow = (spec, field, list) => {
    const mine = lockedEntry(spec, field);
    const deeper = (levelDates || []).filter((e) => e !== mine && e.origin !== "project" && e.field === field && e.value
      && (LEVEL_RANK[e.level] || 0) >= (LEVEL_RANK[spec.level] || 0)
      && list.some((it) => entryCovers(e, it)));
    const manualField = field === "start" ? "start_date_manual" : field === "abs" ? "abs_completion_manual" : "gc_completion_manual";
    const roomsManual = list.filter((it) => it[manualField]).length;
    return { deeper, roomsManual };
  };

  // Editable date cells for a project/building/floor/area group row.
  const renderLevelDateCells = (spec, its) => {
    const roll = roomDates(its) || {};
    const list = (its || []).filter(Boolean);
    const distinct = (f) => new Set(list.map((i) => i[f] || "")).size > 1;
    const conflict = list.some((i) => i.date_conflict);
    const cell = (field, inherited, isMultiAll) => {
      const entry = lockedEntry(spec, field);
      const locked = !!entry;
      const pool = activeRows(list).length ? activeRows(list) : list;
      // Completed work falls out of the rollup: it neither makes a parent read MULTI nor supplies its date.
      const isMulti = isMultiAll && new Set(pool.map((i) => i[`eff_${field}`] || "")).size > 1;
      // Unlocked parent = always the next active date underneath. A manual lock at this exact node wins.
      const summary = locked ? null : mixedSummary(list, field);
      const value = locked ? entry.value : (summary?.date || inherited || "");
      const key = `${spec.level}|${spec.scope_type || ""}|${spec.building || ""}|${spec.floor || ""}|${spec.area || ""}`;
      const node = nodeOf({ level: spec.level, building: spec.building || "", floor: String(spec.floor || ""), area: spec.area || "", scope_type: spec.scope_type || "" });
      const ga = field === "start" ? groupAt(scheduleRel, node) : null;
      const dep = field === "start" ? depInto(scheduleRel, node) : null;
      const { deeper, roomsManual } = locksBelow(spec, field, list);
      const nBelow = deeper.length + (roomsManual ? 1 : 0);
      const lines = deeper.map((e) => `${e.level}${e.scope_type ? ` · ${e.scope_type}` : ""}${e.area ? ` · Area ${e.area}` : e.floor ? ` · Floor ${e.floor}` : e.building ? ` · ${e.building}` : ""}: ${e.value}`);
      if (roomsManual) lines.push(`${roomsManual} room(s) with their own date`);
      const releaseBelow = async () => {
        if (!deeper.length) return;
        if (!window.confirm(`Release ${deeper.length} deeper lock(s) so this level cascades?\n\n${lines.join("\n")}`)) return;
        try {
          await Promise.all(deeper.map((e) => api.post("/frs-dates/level", {
            project_id: projectId, level: e.level, field, value: "", scope_type: e.scope_type || "",
            building: e.building || "", floor: String(e.floor || ""), area: e.area || "", by: currentUser || "" })));
          toast.success(`${deeper.length} lock(s) released — rooms now inherit from above`);
          await Promise.all([loadLevelDates(), load()]);
        } catch (e) { toast.error(e.response?.data?.detail || "Could not release the locks"); }
      };
      const extras = (
        <>
          {!locked && isMulti && (
            <span className="text-[9px] uppercase font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded px-1"
                  title={summary?.date
                    ? `More than one active date sits under this level. Showing ${summary.date}${summary.past ? " — every date below is already behind us" : " — the next one coming up"}. Set a date here to align them.`
                    : "More than one active date sits under this level — set a date here to align them"}>multi</span>
          )}
          {field === "start" && (
            <ScopeLinkControl projectId={projectId} node={spec} scope={spec.scope_type || ""} scopeLabel={list[0]?.scope_code || spec.scope_type}
                              others={scopesAtNode(spec)} items={allItems} rel={scheduleRel} actor={currentUser || ""} view="frs" alwaysShow inline canLink={false}
                              prompt={linkPrompt === key} onDismissPrompt={() => setLinkPrompt("")}
                              onChanged={() => Promise.all([loadLevelDates(), load(), reloadSchedule()])} />
          )}
          {nBelow > 0 && (
            <button type="button" onClick={releaseBelow}
                    className="text-[9px] uppercase font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1 hover:bg-amber-100"
                    data-testid={`level-locks-below-${field}-${key}`}
                    title={`Dates set here will NOT reach rooms held by a deeper lock:\n${lines.join("\n")}\nClick to release the deeper level locks (room dates are left alone).`}>
              {nBelow} lock{nBelow === 1 ? "" : "s"} below
            </button>
          )}
        </>
      );
      return (
        <td key={field} className={`px-1 py-0.5 whitespace-nowrap ${conflict && field !== "start" ? "abs-row-conflict" : ""}`}
            data-testid={`level-date-${field}-${key}`}
            data-locked={locked ? "1" : "0"}
            data-conflict={conflict ? "1" : "0"}
            data-multi={!locked && isMulti ? "1" : "0"}>
          <FrsDateCell value={value || ""} onCommit={(v) => saveLevelDate(spec, field, v)} locked={locked} multi={!locked && isMulti}
                       past={!!summary?.past}
                       hint={locked ? "Locked here — unlock to go back to the next active date underneath"
                         : isMulti ? `Several active dates below — showing ${summary?.date ? `the next one (${summary.date})` : "no scheduled date"}. Pick a date to lock this level and align them.`
                         : "Inherited — pick a date to lock it at this level"}
                       linked={!!ga} paused={!!ga && ga.partners.length === 0 && ga.suspended.length > 0} dep={!!dep}
                       forceOpen={linkPrompt === key && field === "start"} extras={extras}
                       testId={`level-date-input-${field}-${key}`} />
        </td>
      );
    };
    if (onlyStart) return [cell("start", roll.start, distinct("eff_start"))];
    // RA note: ABS Comp is calculated and hidden from the grid — Start and GC Comp only
    return [
      cell("start", roll.start, distinct("eff_start")),
      cell("gc", roll.gc, distinct("eff_gc")),
    ];
  };

  const levelSpecOf = (node, its) => {
    const first = (its || [])[0] || {};
    const lt = node?.levelType;
    if (lt === "scope") return { level: "project", scope_type: first.scope_type || "" };
    if (lt === "building") return { level: "building", scope_type: first.scope_type || "", building: first.building || "" };
    if (lt === "floor") return { level: "floor", scope_type: first.scope_type || "", building: first.building || "", floor: String(first.floor ?? "") };
    if (lt === "area") return { level: "area", scope_type: first.scope_type || "", building: first.building || "", floor: String(first.floor ?? ""), area: first.area || "" };
    return null;
  };


  const renderDateCells = (its, opts = {}) => {
    if (opts.levelSpec) return renderLevelDateCells(opts.levelSpec, its);
    const d = roomDates(its);
    if (!d) return (picking ? ["s", "n"] : onlyStart ? ["s"] : ["s", "g"]).map((k) => (
      <td key={k} className="px-2 py-1.5" />));
    const editable = opts.editable !== false && !d.multiScope;
    // A finished room is frozen: every one of its dates stays locked — until someone unlocks it
    // on purpose (recorded). Only a GENUINELY 100% room (server-resolved) counts, never a stale
    // readiness flag on its own.
    const roomDone = d.frozen === true || (d.frozen === undefined && (its || []).length > 0 && (its || []).every((i) => i.readiness_status === "completed"));
    const o = d.origin || {};
    const first = (its || [])[0] || {};
    const key = d.origin
      ? `${o.scope_type}|${o.building}|${o.floor}|${o.area}|${o.room_number}|${o.room_type}`
      : `multi|${first.building || ""}|${first.floor || ""}|${first.area || ""}|${first.room_number || ""}|${first.room_type || ""}`;
    const roomNode = d.origin ? nodeOf({ level: "room", building: o.building || "", floor: String(o.floor ?? ""), area: o.area || "",
      room_number: o.room_number || "", room_type: o.room_type || "", scope_type: o.scope_type || "" }) : null;
    const roomGa = roomNode ? groupAt(scheduleRel, roomNode) : null;
    const roomDep = roomNode ? depInto(scheduleRel, roomNode) : null;
    const cell = (field, value, source, extra = null) => {
      const isLocked = roomDone || source === "manual" || source === "completed";
      const frozen = roomDone || (source === "completed" && !d.unlocked);
      const hint = frozen ? "Locked because this room is 100% complete — a finished room's dates must not drift."
        : d.unlocked ? `Completed room — dates unlocked by ${d.unlocked.by || "someone"} on ${String(d.unlocked.at || "").slice(0, 10)}`
          : source === "manual" ? "Locked here — unlock to re-inherit from the level above"
            : `Inherited from ${source || "above"} — pick a date to lock it on this room`;
      const marks = field === "start" && (roomGa || roomDep) ? (
        <>
          {roomGa && <span className="inline-flex items-center gap-0.5 rounded-full border border-teal-300 bg-teal-50 px-1.5 text-[9px] font-semibold uppercase text-teal-800" data-testid={`frs-date-linked-${key}`}>
            <Link2 className="w-2.5 h-2.5" />{roomGa.partners.join(", ")}{roomGa.suspended.length ? <span className="line-through opacity-50">{roomGa.partners.length ? ", " : ""}{roomGa.suspended.join(", ")}</span> : null}
            <span className="font-normal normal-case text-teal-600"> · set at {pathLabel(roomGa.at) || "whole scope"}</span></span>}
          {roomDep && <span className="inline-flex items-center gap-0.5 rounded-full border border-indigo-300 bg-indigo-50 px-1.5 text-[9px] font-semibold text-indigo-800" data-testid={`frs-date-dep-${key}`}>
            <ArrowRightToLine className="w-2.5 h-2.5" />{pathLabel(roomDep.pred)} {roomDep.pred.scope_type} +{roomDep.offset_days}wd</span>}
        </>
      ) : null;
      return (
        <td
          key={field}
          className={`px-1 py-0.5 whitespace-nowrap ${d.conflict && field !== "start" ? "abs-row-conflict" : ""}`}
          data-testid={`frs-date-${field}-${key}`}
          data-source={source || ""}
        >
          <div className="flex items-center gap-0.5">
            {editable ? (
              <FrsDateCell value={value || ""} locked={isLocked} canUnlock={source === "manual" && !frozen} readOnly={frozen} hint={hint}
                           onCommit={(v) => !roomDone && d.origin && onDateChange(d.origin, field, value, v, key)}
                           linked={field === "start" && !!roomGa} paused={!!roomGa && roomGa.partners.length === 0 && roomGa.suspended.length > 0} dep={field === "start" && !!roomDep}
                           extras={marks || extra ? <>{marks}{extra}</> : null}
                           testId={`frs-date-input-${field}-${key}`} />
            ) : (
              <span className="text-[11px] text-gray-600">{fmtDMY(value) || "—"}</span>
            )}
            {d.multiScope && field === "start" && (
              <span className="text-[9px] uppercase font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded px-1"
                title="This room has more than one scope — filter to a single scope to edit its dates">multi</span>
            )}
          </div>
        </td>
      );
    };
    if (picking) {
      // Picking shows only what a foreman needs: when work starts and when the
      // material has to be on site.
      return [cell("start", d.start, d.startSource), cell("need", d.need, d.needSource)];
    }
    if (onlyStart) return [cell("start", d.start, d.startSource)];
    return [
      cell("start", d.start, d.startSource),
      cell("gc", d.gc, d.gcSource),
    ];
  };

  const groups = useMemo(() => {
    let src = filters.change_orders_only ? items.filter((i) => i.is_delta) : items;
    if (filters.short_only) {
      // only the rooms holding material the warehouse could not provide
      const short = new Set(Object.entries(cellState.lines || {})
        .filter(([, v]) => Number(v.short || 0) > 0).map(([k]) => k));
      const rkey = (i) => `${i.building || ""}||${i.floor || ""}||${i.room_number || ""}||${i.room_type || ""}`;
      const rooms = new Set(src.filter((i) => short.has(i.frs_item_key)).map(rkey));
      src = src.filter((i) => rooms.has(rkey(i)));
    }
    return buildGroups(src);
  }, [items, filters.change_orders_only, filters.short_only, cellState]);

  // Build scope filter options: label = scope CODE (e.g., "TRA"), value =
  // scope_type (e.g., "Toilet Room Accessories") so existing filter logic
  // keeps matching against scope_type. Resolution priority:
  //   1. item.scope_code if present
  //   2. scope_codes collection (trade → code)
  //   3. Derived acronym from scope_type (first-letter-of-each-word, max 3)
  const scopeCodeByTrade = useMemo(() => {
    const map = {};
    for (const sc of scopeCodes) {
      if (sc.trade && sc.code) map[sc.trade.toLowerCase()] = sc.code;
    }
    return map;
  }, [scopeCodes]);
  const scopeOptions = useMemo(() => {
    const byType = new Map();
    const resolveCode = (st, itemCode = "") => {
      if (itemCode) return itemCode;
      const fromMap = scopeCodeByTrade[(st || "").toLowerCase()];
      if (fromMap) return fromMap;
      const words = (st || "").split(/\s+/).filter(Boolean);
      // Single-word scopes (BIKE, FPS, POST, TC…) should stay whole, not be
      // reduced to their first letter. Multi-word scopes still become
      // first-letter acronyms (Toilet Room Accessories → TRA).
      if (words.length <= 1) return (words[0] || "").slice(0, 6).toUpperCase();
      return words.map((w) => w[0] || "").join("").slice(0, 3).toUpperCase();
    };
    // Cascade: only scopes still present after the other active filters.
    const visibleScopes = new Set(optionsFor("scope_type"));
    for (const it of allItems) {
      const st = it.scope_type || "";
      if (!st || byType.has(st) || !visibleScopes.has(st)) continue;
      byType.set(st, resolveCode(st, it.scope_code));
    }
    for (const st of visibleScopes) {
      if (!byType.has(st)) byType.set(st, resolveCode(st));
    }
    return [...byType.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allItems, JSON.stringify(filters), scopeCodeByTrade]);
  // scope_type → short code, for chips.
  const scopeLabelByType = useMemo(() => {
    const m = {};
    for (const o of scopeOptions) m[o.value] = o.label;
    return m;
  }, [scopeOptions]);
  // Active-filter chips (dimension + value, each removable).
  const activeChips = useMemo(() => {
    const LABELS = {
      scope_type: "Scope", building: "Building", floor: "Floor", area: "Area",
      room: "Room", room_type: "Room Type", readiness_status: "Readiness",
      mobilization_status: "Status",
    };
    const out = [];
    for (const dim of Object.keys(LABELS)) {
      for (const v of (filters[dim] || [])) {
        let label = v;
        if (dim === "scope_type") label = scopeLabelByType[v] || v;
        else if (["building", "floor", "area"].includes(dim)) label = formatLevelLabel(dim, v);
        else label = String(v).replace(/_/g, " ");
        out.push({ dim, value: v, label: `${LABELS[dim]}: ${label}` });
      }
    }
    if ((filters.search || "").trim()) {
      out.push({ dim: "search", value: filters.search, label: `Search: ${filters.search}` });
    }
    return out;
  }, [JSON.stringify(filters), scopeLabelByType]);
  const rows = useMemo(() => flattenVisible(groups, expanded), [groups, expanded]);
  // Current user (used as `author` when adding new group notes) — defined near the top of the component.
  const fieldRole = (() => {
    try { return window.__fieldRole || ""; } catch { return ""; }
  })();
  // Callback fired by GroupNoteCell after a save / update / delete so the
  // table re-renders with the latest note text without a full refetch.
  const onGroupNoteSaved = (key, note) => {
    setGroupNotesByKey((prev) => {
      const next = { ...prev };
      if (note) next[key] = note;
      else delete next[key];
      return next;
    });
  };
  // Indent applied to the "Description" column (both the column header and
  // the FRS line item rows) so the description text visually nests under the
  // room dropdown — matches the chevron/icon offset used by group rows.
  // Formula: (parent_room.depth) * 18 + chevron(20) + icon(14) + gap(6).
  // We sample any visible item row OR fall back to the deepest room in the
  // current `rows` so the header stays aligned even when rooms are still
  // collapsed.
  const itemIndentPx = useMemo(() => {
    const sampleItem = rows.find((r) => r.kind === "item");
    if (sampleItem) return (sampleItem.depth - 1) * 18 + 40;
    let deepestRoom = 0;
    for (const r of rows) {
      if (r.kind === "group" && r.node.levelType === "room") {
        if (r.node.depth > deepestRoom) deepestRoom = r.node.depth;
      }
    }
    return deepestRoom > 0 ? deepestRoom * 18 + 40 : 40;
  }, [rows]);

  const collectAllPaths = () => {
    const all = new Set();
    const walk = (nodes) => {
      for (const n of nodes) {
        all.add(n.path);
        if (n.children?.length) walk(n.children);
      }
    };
    walk(groups);
    return all;
  };

  useEffect(() => {
    if (!expandedView) return;
    const onKey = (e) => { if (e.key === "Escape") setExpandedView(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expandedView]);

  // Field view, expanded: the FRS owns the whole phone screen — hide the Mira bubble too.
  useEffect(() => {
    const on = installMode && expandedView;
    document.body.classList.toggle("frs-field-expanded", on);
    return () => document.body.classList.remove("frs-field-expanded");
  }, [installMode, expandedView]);
  useEffect(() => { delivery?.onExpanded?.(expandedView); }, [expandedView]);   // eslint-disable-line
  // Expanded delivery on a phone: only Bulk drop / Spread / Not delivered / Place-move stay above
  // the matrix — search, the tree strip, the filter chips and the project-wide tools all step aside.
  const leanDelivery = !!delivery && expandedView && phoneNarrow;

  const expandAll = () => setExpanded(collectAllPaths());
  // Dispatched rows arrive fully expanded; everything else stays collapsed.
  useEffect(() => {
    if (focusActive) {
      setExpanded(collectAllPaths());
      setGridExpandSignal((n) => n + 1);
    }
    // eslint-disable-next-line
  }, [focusActive, allItems.length]);
  const collapseAll = () => setExpanded(new Set());
  // Searching opens everything down to the rows that matched, then highlights the text
  const searchQ = (filters.search || "").trim();
  useEffect(() => {
    if (!searchQ) return;
    const t = setTimeout(() => { setExpanded(collectAllPaths()); setGridExpandSignal((n) => n + 1); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [searchQ]);
  const rootRef = useRef(null);
  useTextHighlight(rootRef, searchParsed.phrases);
  // PLAN-COMPLETE — marks tapped on a drawing open this matrix filtered to a handful of rooms.
  // They must be OPEN: a pending change the installer cannot see is no use. The dispatch effect
  // above fires before the grouped tree has settled on this path, so this one re-runs off the
  // FILTERED rows, a tick later.
  useEffect(() => {
    if (!autoExpand || !items.length) return undefined;
    const t = setTimeout(() => { setExpanded(collectAllPaths()); setGridExpandSignal((n) => n + 1); }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [autoExpand, items.length]);
  const setsEqual = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));
  const toggleExpandAll = () => {
    const all = collectAllPaths();
    const fullyExpanded = all.size > 0 && setsEqual(expanded, all);
    setExpanded(fullyExpanded ? new Set() : all);
  };
  const pathsToLevel = (lvl) => {
    const set = new Set();
    const walk = (nodes) => {
      for (const n of nodes) {
        if (n.depth < lvl) set.add(n.path);
        if (n.children?.length) walk(n.children);
      }
    };
    walk(groups);
    return set;
  };
  // Level buttons open the tree so that level's rows are showing (and everything above is open)
  const TREE_ORDER = ["scope", "building", "floor", "area", "room"];
  const pathsShowing = (type) => {
    const cut = TREE_ORDER.indexOf(type);
    const set = new Set();
    // Merged single-child rows read as their deepest level (Building › Floor shows the floor).
    const levelOf = (n) => (n.labelChain?.length ? n.labelChain[n.labelChain.length - 1].levelType : n.levelType);
    const walk = (nodes) => { for (const n of nodes) { if (TREE_ORDER.indexOf(levelOf(n)) < cut) set.add(n.path); if (n.children?.length) walk(n.children); } };
    walk(groups);
    return set;
  };
  const [treeLevelPick, setTreeLevelPick] = useState("");
  const expandToType = (type) => {
    setExpanded(type === "room" ? collectAllPaths() : pathsShowing(type));
    setTreeLevelPick(type);
  };
  const expandToLevel = (lvl) => {
    const target = pathsToLevel(lvl);
    const all = collectAllPaths();
    // Toggle: if already at this exact level, flip to fully expanded.
    setExpanded(setsEqual(expanded, target) ? all : target);
  };

  const toggleNode = (path) => {
    const ns = new Set(expanded);
    if (ns.has(path)) ns.delete(path); else ns.add(path);
    setExpanded(ns);
  };
  // FRS items in any active mob pipeline are LOCKED — checkboxes cannot be
  // toggled and clicking them is a no-op. Use the group-level Recall button
  // (on Building / Floor / Area / Room rows) to break the link with the mob
  // and free the items for re-request.
  const PIPELINE_STATES = ["requested", "scheduled", "s_pool", "d_pool", "dispatched", "ready_to_dispatch"];
  const isLocked = (it) => PIPELINE_STATES.includes(it?.mobilization_status);

  // Compact accessory summary for higher-level group rows (building / floor /
  // area). Aggregates total quantities by product description so the foreman
  // can see at a glance "Floor 1: 1 Toilet Paper Holder · 2 Mirror · 3 Soap
  // Dispenser" without expanding.
  const accessorySummary = (node) => {
    const ids = new Set(node.allItemIds);
    const acc = new Map();
    for (const it of items) {
      if (!ids.has(it.id)) continue;
      const { part, short } = headerLabels(it);
      const desc = [part, short].filter(Boolean).join(" · ").trim();
      if (!desc) continue;
      acc.set(desc, (acc.get(desc) || 0) + Number(it.quantity || 0));
    }
    return [...acc.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([d, q]) => ({ d, q }));
  };
  // ready count, and recallable lock count.
  const groupAgg = (node) => {
    const ids = new Set(node.allItemIds);
    const its = items.filter((x) => ids.has(x.id));
    const statusSet = new Set();
    let lockedCount = 0;
    let readyCount = 0;
    // Room rollup (for the tooltip wording — matches the confirm dialog).
    const roomsTotal = new Set();
    const roomsNotReady = new Set();
    for (const it of its) {
      const ms = it.mobilization_status || "none";
      statusSet.add(ms);
      if (PIPELINE_STATES.includes(ms)) lockedCount += 1;
      const rk = `${it.building || ""}||${it.floor || ""}||${it.room_number || ""}||${it.room_type || ""}`;
      roomsTotal.add(rk);
      if (READY_VALUES.includes(it.readiness_status || "not_ready")) readyCount += 1;
      else roomsNotReady.add(rk);
    }
    // Only surface a pipeline status pill on the parent row when EVERY child
    // shares the same non-"none" status. Any mix → leave blank so foremen
    // don't get a misleading rollup.
    let topStatus = "none";
    if (statusSet.size === 1) {
      const only = [...statusSet][0];
      if (only !== "none") topStatus = only;
    }
    const allReady = its.length > 0 && readyCount === its.length;
    const allNotReady = its.length > 0 && readyCount === 0;
    return {
      total: its.length,
      lockedCount,
      readyCount,
      roomsTotal: roomsTotal.size,
      roomsReady: roomsTotal.size - roomsNotReady.size,
      topStatus,
      // Readiness states: "ready" (all items ready), "not_ready" (none ready),
      // "partial" (some ready, some not). The toggle button cycles
      // partial/not_ready → all-ready → all-not-ready → all-ready.
      readinessState: allReady ? "ready" : allNotReady ? "not_ready" : "partial",
      allReady,
    };
  };

  const toggleSelected = (id) => {
    const it = items.find((x) => x.id === id);
    if (it && isLocked(it)) {
      toast.error("This item is locked — already in a mobilization. Use the group-level Recall button to release it first.");
      return;
    }
    const ns = new Set(selected);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    setSelected(ns);
  };

  // Toggle readiness for an entire group at once.
  // Room-level readiness (frs_items.readiness_status) is the ONLY stored
  // state — group rows derive their dot from their children, and setting a
  // group applies the value to every room underneath it.
  // If the group is at building/floor/area level AND the data has items of
  // OTHER scopes that occupy the same location, ask the user whether they
  // want to apply the readiness change across ALL scopes in that location.
  const roomCountOf = (ids) => {
    const set = new Set(ids);
    const keys = new Set();
    for (const it of allItems) {
      if (!set.has(it.id)) continue;
      keys.add(`${it.building || ""}||${it.floor || ""}||${it.room_number || ""}||${it.room_type || ""}`);
    }
    return keys.size;
  };
  // readiness_status also carries the richer "scheduled" / "completed" states
  // that the grid shading depends on, so the readiness toggle only ever moves
  // rows between not_ready ↔ ready and leaves scheduled/completed alone.
  const effectiveIds = (ids, makeReady) => {
    const byId = new Map(allItems.map((it) => [it.id, it]));
    const cur = (id) => byId.get(id)?.readiness_status || "not_ready";
    return makeReady
      ? ids.filter((id) => cur(id) === "not_ready")
      : ids.filter((id) => cur(id) === "ready");
  };
  const applyReadiness = async (ids, makeReady) => {
    const status = makeReady ? "ready" : "not_ready";
    const target = effectiveIds(ids, makeReady);
    if (target.length === 0) {
      toast.info(makeReady
        ? "Nothing to change — already ready (or scheduled / completed)"
        : "Nothing to change — rooms are already not ready, scheduled or completed");
      return;
    }
    try {
      // The backend runs the auto-request scan on the same call: Ready work
      // starting inside the configured window becomes a Schedule Pool card
      // without anyone clicking Request.
      const { data } = await api.post("/frs-items/bulk-update", { ids: target, readiness_status: status });
      const rooms = roomCountOf(target);
      toast.success(`${rooms} room${rooms === 1 ? "" : "s"} → ${makeReady ? "Ready" : "Not Ready"}`);
      if (data?.auto_requests_created > 0) {
        toast.success(`${data.auto_requests_created} card${data.auto_requests_created === 1 ? "" : "s"} auto-requested`, {
          description: "Ready work starting inside the auto-request window",
        });
      }
      load();
      if (makeReady) checkCoverageAfterReady(target);
    } catch { toast.error("Update failed"); }
  };
  // FLD-1 addendum — Ready is never blocked; the hook simply asks afterwards
  // whether the uncovered products should go on a picking ticket.
  const checkCoverageAfterReady = async (ids) => {
    const rows = allItems.filter((it) => ids.includes(it.id));
    const roomKeys = [...new Set(rows.map((it) =>
      [it.building || "", it.floor || "", it.room_number || ""].join("|")))];
    if (roomKeys.length === 0) return;
    try {
      const { data } = await api.post("/material/coverage",
        { project_id: projectId, room_keys: roomKeys });
      const uncovered = data.uncovered || [];
      if (uncovered.length === 0) return;
      const products = data.uncovered_products || [];
      const label = roomKeys.length === 1
        ? `Room ${[rows[0]?.room_number, rows[0]?.room_name].filter(Boolean).join(" · ")}`
        : `${roomKeys.length} rooms`;
      toast.custom((t) => (
        <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-lg p-3 flex flex-col gap-2 max-w-md"
             data-testid="readiness-coverage-prompt">
          <span className="text-sm text-gray-800 dark:text-neutral-100">
            {label} has <strong>{products.length} product(s)</strong> not on a pull ticket
            <span className="text-gray-500"> ({products.slice(0, 6).join(", ")}{products.length > 6 ? "…" : ""})</span>.
          </span>
          <div className="flex justify-end gap-2 flex-wrap">
            <button data-testid="readiness-coverage-skip"
                    onClick={async () => {
                      toast.dismiss(t);
                      await api.post("/material/coverage/skip", {
                        project_id: projectId, room_keys: roomKeys, actor: currentUser || "",
                        note: "Skipped at readiness" });
                      toast.info("Skipped — the rooms stay flagged until they are covered");
                    }}
                    className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-neutral-700">Skip</button>
            <button data-testid="readiness-coverage-create"
                    onClick={() => {
                      toast.dismiss(t);
                      loadMaterial();
                      setRequestOpen({
                        selection: uncovered.map((u) => ({
                          frs_item_key: u.frs_item_key, qty: u.uncovered,
                          room_key: u.room_key, product_uid: u.product_uid,
                          product_short: u.product_short })),
                        note: "only the uncovered quantities are preselected",
                      });
                    }}
                    className="px-2 py-1 text-xs rounded bg-blue-600 text-white">Create a pull ticket</button>
          </div>
        </div>
      ), { duration: 20000 });
    } catch { /* the hook never blocks readiness */ }
  };
  // Any readiness change touching more than 10 rooms asks first.
  const confirmReadiness = (ids, makeReady, whatLabel = "") => {
    if (!ids || ids.length === 0) return;
    const rooms = roomCountOf(effectiveIds(ids, makeReady));
    if (rooms <= 10) { applyReadiness(ids, makeReady); return; }
    toast.custom((t) => (
      <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-lg p-3 flex flex-col gap-2 max-w-md" data-testid="readiness-confirm-dialog">
        <span className="text-sm text-gray-800 dark:text-neutral-100">
          Mark <strong>{rooms} rooms</strong>{whatLabel ? <> in {whatLabel}</> : null} as{" "}
          <strong>{makeReady ? "Ready" : "Not Ready"}</strong>?
        </span>
        <div className="flex justify-end gap-2">
          <button onClick={() => toast.dismiss(t)} data-testid="readiness-confirm-cancel" className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800">Cancel</button>
          <button
            onClick={() => { toast.dismiss(t); applyReadiness(ids, makeReady); }}
            data-testid="readiness-confirm-apply"
            className="px-2 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white"
          >
            Yes, mark {makeReady ? "Ready" : "Not Ready"}
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };
  const toggleGroupReadiness = async (node, makeReady) => {
    const chainLabel = (node.labelChain || [{ levelType: node.levelType, key: node.key }])
      .map((l) => formatLevelLabel(l.levelType, l.key)).join(" › ");
    // Scope level: applies to every room of that scope in the whole project
    // (not just the rows currently visible through the filters).
    if (node.levelType === "scope") {
      const scope = node.key === "Unspecified" ? "" : node.key;
      const ids = allItems.filter((it) => (it.scope_type || "Unspecified") === node.key || (!it.scope_type && scope === "")).map((it) => it.id);
      confirmReadiness(ids, makeReady, `scope ${chainLabel}`);
      return;
    }
    const apply = (ids) => confirmReadiness(ids, makeReady, chainLabel);
    // Detect cross-scope items at the SAME location for building/floor/area
    // levels. We compare items by (building, floor, area) keys when those
    // levels are present in the parent path.
    const crossScopeIds = (() => {
      if (!["building", "floor", "area"].includes(node.levelType)) return [];
      // Folded single-child chains are narrower than their top level (they
      // already drill down to one room), so the "other scopes at this
      // location" prompt would over-select — skip it there.
      if ((node.mergedPaths || []).length > 0) return [];
      const inGroup = new Set(node.allItemIds);
      const sample = items.find((i) => inGroup.has(i.id));
      if (!sample) return [];
      const sampleScope = sample.scope_type || "Unspecified";
      // Items at the same location (not narrower) that aren't already in the group.
      const matches = items.filter((it) => {
        if (inGroup.has(it.id)) return false;
        if ((it.scope_type || "Unspecified") === sampleScope) return false;
        // Match progressively up: building level → match by building only;
        // floor level → match by building+floor; area level → all three.
        if (node.levelType === "building" && it.building !== sample.building) return false;
        if (node.levelType === "floor"    && (it.building !== sample.building || String(it.floor) !== String(sample.floor))) return false;
        if (node.levelType === "area"     && (it.building !== sample.building || String(it.floor) !== String(sample.floor) || it.area !== sample.area)) return false;
        return true;
      });
      return matches.map((m) => m.id);
    })();
    if (crossScopeIds.length === 0) {
      apply(node.allItemIds);
      return;
    }
    // Distinct cross-scope names for the prompt
    const otherScopes = [...new Set(items.filter((it) => crossScopeIds.includes(it.id)).map((it) => it.scope_type || "Unspecified"))];
    toast.custom((t) => (
      <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-lg p-3 flex flex-col gap-2 max-w-md" data-testid="readiness-crossscope-dialog">
        <span className="text-sm text-gray-800 dark:text-neutral-100">
          This <strong>{node.levelType}</strong> also has <strong>{crossScopeIds.length}</strong> item{crossScopeIds.length === 1 ? "" : "s"} from other scope{otherScopes.length === 1 ? "" : "s"} ({otherScopes.join(", ")}).
          <br />Apply <strong>{makeReady ? "Ready" : "Not Ready"}</strong> to all scopes here, or just this scope?
        </span>
        <div className="flex justify-end gap-2 flex-wrap">
          <button onClick={() => toast.dismiss(t)} data-testid="readiness-crossscope-cancel" className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800">Cancel</button>
          <button onClick={() => { toast.dismiss(t); apply(node.allItemIds); }} data-testid="readiness-crossscope-thisscope" className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800">Just this scope</button>
          <button onClick={() => { toast.dismiss(t); apply([...node.allItemIds, ...crossScopeIds]); }} data-testid="readiness-crossscope-allscopes" className="px-2 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white">All scopes</button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  // Recall an entire group's items. Resets each linked item to "blank/draft"
  // (mobilization_status='none', mobilization_id='', previously_scheduled=false).
  const recallGroup = (node) => {
    const ids = node.allItemIds.filter((id) => {
      const it = items.find((x) => x.id === id);
      return it && isLocked(it);
    });
    if (ids.length === 0) return;
    toast.custom((t) => (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 flex flex-col gap-2 max-w-sm">
        <span className="text-sm text-gray-800 dark:text-neutral-100">Recall {ids.length} item{ids.length === 1 ? "" : "s"} from {node.levelType} <strong>{node.key}</strong>? Status will reset to blank (draft).</span>
        <div className="flex justify-end gap-2">
          <button onClick={() => toast.dismiss(t)} className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800">Cancel</button>
          <button onClick={async () => {
            toast.dismiss(t);
            try {
              await api.post("/frs-items/recall", { ids });
              toast.success(`Recalled ${ids.length} item${ids.length === 1 ? "" : "s"} → blank`);
              load();
            } catch { toast.error("Recall failed"); }
          }} className="px-2 py-1 text-xs rounded bg-amber-600 hover:bg-amber-700 text-white">Recall</button>
        </div>
      </div>
    ), { duration: Infinity });
  };
  // Backwards-compat shim — earlier per-line handler is no longer wired but
  // a few code paths might still reference it. Now delegates to a single-id
  // recall.
  const recallItem = async (it) => {
    toast.custom((t) => (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 flex flex-col gap-2 max-w-sm">
        <span className="text-sm text-gray-800 dark:text-neutral-100">Recall this item? Status will reset to blank (draft).</span>
        <div className="flex justify-end gap-2">
          <button onClick={() => toast.dismiss(t)} className="px-2 py-1 text-xs rounded border border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-800">Cancel</button>
          <button onClick={async () => {
            toast.dismiss(t);
            try {
              await api.post("/frs-items/recall", { ids: [it.id] });
              toast.success("Item recalled — back to blank");
              load();
            } catch { toast.error("Recall failed"); }
          }} className="px-2 py-1 text-xs rounded bg-amber-600 hover:bg-amber-700 text-white">Recall</button>
        </div>
      </div>
    ), { duration: Infinity });
  };
  const toggleSelectAllDescendants = (node) => {
    const ns = new Set(selected);
    const all = node.allItemIds;
    const allOn = all.length > 0 && all.every((id) => ns.has(id));
    if (allOn) all.forEach((id) => ns.delete(id));
    else all.forEach((id) => ns.add(id));
    setSelected(ns);
  };

  // Mark Ready / Unmark Ready toolbar actions were removed — the group-level
  // Readiness pills (3-way: Ready / Partial / Not Ready) on every Building /
  // Floor / Area / Room row now do the same job and avoid the duplicate UI.
  const openRequestDialog = (idsOverride) => {
    const selIds = Array.isArray(idsOverride) ? idsOverride : [...selected];
    if (selIds.length === 0) return;
    // Smart title from current selection (e.g., "TRA-BA-F2" or "TRA-8rms").
    const selSet = new Set(selIds);
    const selItems = allItems.filter((i) => selSet.has(i.id));
    const scopes = [...new Set(selItems.map((i) => i.scope_type || "").filter(Boolean))];
    const firstScope = scopes[0] || "";
    setReqScope(firstScope);
    const { title: suggested } = smartTitleFromItems(
      firstScope ? selItems.filter((i) => (i.scope_type || "") === firstScope) : selItems,
    );
    setMobForm({
      title: suggested,
      requested_start_date: "",
      requested_end_date: "",
      estimated_duration_hours: 8,
      foreman_notes: "",
      priority: "normal",
      num_installers: 1,
      preferred_installer_ids: [],
      badged_required: !!project?.default_badged_required,
      drug_test_required: !!project?.default_drug_test_required,
      site_familiarity_required: !!project?.default_site_familiarity_required,
      is_punchlist: false,
      foreman_preference_notes: "",
    });
    // Stash selected ids in the form for submit
    setMobForm((f) => ({ ...f, _selIds: selIds }));
    // Refresh project notes + mobs so the Lineout validation is up-to-date
    // even if other tabs have added notes since this page first loaded.
    loadGroupNotes();
    loadProjectMobs();
    setLineoutDrafts({});
    setReqOpen(true);
  };

  const autoReqDone = useRef(false);
  useEffect(() => {
    if (!autoRequestScope || autoReqDone.current || !allItems.length || !memoryApplied) return;
    autoReqDone.current = true;
    const ids = allItems.filter((i) => (i.scope_type === autoRequestScope || i.scope_code === autoRequestScope)
      && !i.is_delta && !i.is_punch && Number(i.percent_complete || 0) < 1).map((i) => i.id);
    onAutoRequestOpened?.();
    if (!ids.length) { toast.info(`Every ${autoRequestScope} room is complete — nothing to request`); return; }
    // the dialog reads the FILTERED rows — show the mob's scope so its rooms are in view and counted
    const st = allItems.find((i) => i.id === ids[0])?.scope_type || autoRequestScope;
    setFilterDim("scope_type", [st]);
    setSelected(new Set(ids));
    openRequestDialog(ids);
  }, [allItems, autoRequestScope, memoryApplied]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedItems = useMemo(() => items.filter((i) => selected.has(i.id)), [items, selected]);
  const selectedTotalQty = selectedItems.reduce((s, i) => s + (i.quantity || 0), 0);
  // Rows of the scope the request dialog is currently working on (1 card = 1 scope).
  const scopeOf = (id) => items.find((i) => i.id === id)?.scope_type || "";
  const scopedItems = useMemo(
    () => (reqScope ? selectedItems.filter((i) => (i.scope_type || "") === reqScope) : selectedItems),
    [selectedItems, reqScope],
  );
  const selectedScopes = useMemo(
    () => [...new Set(selectedItems.map((i) => i.scope_type || "").filter(Boolean))],
    [selectedItems],
  );

  const summary = useMemo(() => {
    const rs = new Set();
    let total = 0;
    let ready = 0;
    for (const it of items) {
      rs.add(`${it.building || ""}||${it.floor || ""}||${it.room_number || ""}||${it.room_type || ""}`);
      const q = it.quantity || 0;
      total += q;
      if (["ready", "scheduled", "completed"].includes(it.readiness_status)) ready += q;
    }
    return { rooms: rs.size, total, ready };
  }, [items]);

  const submitRequest = async () => {
    const allIds = mobForm._selIds || [...selected];
    // ONE request = ONE scope. When the selection spans several scopes the
    // dialog walks them one at a time (active scope = reqScope) so the lineout
    // gate, daily target and materials always match the card being created.
    const ids = reqScope ? allIds.filter((id) => scopeOf(id) === reqScope) : allIds;
    if (!ids || ids.length === 0) { toast.error("No items selected"); return; }
    if (!mobForm.title) { toast.error("Title required"); return; }
    if (mobForm._isFirstMob && (mobForm._missingKeys || []).length > 0) {
      toast.error(`Lineout incomplete — add notes to: ${(mobForm._missingKeys || []).join(", ")}`);
      return;
    }
    // Auto-save any required lineout note the foreman typed into the box but
    // didn't explicitly save with "Add Note" — a typed draft should never
    // block the request.
    const noteScope = reqScope || (items.find((i) => ids.includes(i.id))?.scope_type) || "";
    try {
      for (const f of REQUIRED_LINEOUT_FIELDS) {
        const draft = (lineoutDrafts[f.key] || "").trim();
        if (!draft) continue;
        const scopeForSave = f.level === "project" ? "" : noteScope;
        const alreadySaved = projectNotes.some((n) =>
          (n.key || "") === f.key && (n.scope_type || "") === scopeForSave && (n.text || "").trim());
        if (alreadySaved) continue;
        const r = await api.post("/notes", {
          project_id: projectId, scope_type: scopeForSave, key: f.key,
          text: draft, author: currentUser || "",
        });
        setProjectNotes((cur) => [r.data, ...cur]);
      }
    } catch {
      toast.error("Could not save the lineout note — try again");
      return;
    }
    try {
      // One Schedule Pool card for THIS scope — the backend applies the card
      // defaults (dates from Cluster 5, duration + installers from Cluster 3).
      await api.post("/requests", {
        project_id: projectId,
        frs_item_ids: ids,
        priority: mobForm.priority,
        title: mobForm.title,
      });
      const remaining = allIds.filter((id) => !ids.includes(id));
      const nextScope = remaining.map(scopeOf).find(Boolean) || "";
      setSelected(new Set(remaining));
      setLineoutDrafts({});
      if (nextScope) {
        toast.success(`Card created for ${reqScope} — now finish ${nextScope}`);
        setReqScope(nextScope);
        setMobForm((f) => ({
          ...f, _selIds: remaining,
          title: smartTitleFromItems(items.filter((i) => remaining.includes(i.id) && i.scope_type === nextScope)).title,
        }));
        loadProjectMobs();
      } else {
        toast.success(`Request card created — ${reqScope || "1 scope"}`);
        setReqOpen(false);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create request");
    }
  };

  const runAutoScan = async () => {
    setAutoScanning(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/auto-requests/scan`);
      if (data.created > 0) {
        toast.success(`${data.created} card${data.created === 1 ? "" : "s"} auto-requested`, {
          description: `Ready work starting within ${data.window_weeks} weeks — one card per scope`,
        });
        load();
      } else {
        toast.info(`Nothing to auto-request — no Ready rooms start within ${data.window_weeks} weeks`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-request scan failed");
    }
    setAutoScanning(false);
  };

  useEffect(() => {
    if (!resetUndo) return;
    const picked = Object.entries(filters).some(([k, v]) => (Array.isArray(v) ? v.length : k === "search" ? !!v : !!v));
    if (picked) setResetUndo(null);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  const clearFilters = () => setFilters({ building: [], area: [], floor: [], room: [], room_type: [], scope_type: [], readiness_status: [], mobilization_status: [], search: "", change_orders_only: false, short_only: false });

  // Tree expand-level controls + totals — one strip in the office view, folded into the compact bar in the field view.
  const renderTreeControls = () => {
    const allTreePaths = collectAllPaths();
    const fullyExpanded = allTreePaths.size > 0 && setsEqual(expanded, allTreePaths);
    const treeHas = (type) => allItems.some((i) => String(i[type] ?? "").trim() !== "");
    // Which depth the tree is opened to — that level button stays highlighted
    // Single-child chains merge in the tree, so several levels can share one state — the one clicked wins
    const treeLevel = expanded.size === 0 ? ""
      : treeLevelPick && (setsEqual(expanded, pathsShowing(treeLevelPick)) || (treeLevelPick === "room" && fullyExpanded)) ? treeLevelPick
      : ["room", "area", "floor", "building"].find((t) => setsEqual(expanded, pathsShowing(t))) || (fullyExpanded ? "room" : "");
    return (
      <>
        <button onClick={toggleExpandAll} className="grid h-6 w-6 place-items-center rounded border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800" data-testid="tree-expand-all"
                title={fullyExpanded ? "Collapse all" : "Expand all"} aria-label={fullyExpanded ? "Collapse all" : "Expand all"}>
          {fullyExpanded ? <ChevronsDownUp className="h-3.5 w-3.5" /> : <ChevronsUpDown className="h-3.5 w-3.5" />}
        </button>
        {!delivery && <>
        <span className="text-gray-300">·</span>
        <ScopePills testId="tree-scope-pills" options={scopeOptions} value={filters.scope_type || []} onChange={(v) => setFilterDim("scope_type", v)} />
        </>}
        {selectedTotalQty > 0 && (
          <span className="text-gray-700 font-medium" data-testid="tree-selected-count-strip">
            <span className="text-gray-900 font-semibold">{selectedTotalQty}</span> selected
          </span>
        )}
        <span className="ml-auto" data-testid="tree-summary">
          {summary.rooms} rooms · {summary.total} units · {summary.ready} of {summary.total} ready
        </span>
      </>
    );
  };

  return (
    <div
      ref={rootRef}
      className={expandedView
        ? (installMode
          ? "fixed inset-0 z-50 !m-0 bg-gray-50 dark:bg-gray-900 overflow-auto frs-field-shell space-y-0 sm:space-y-3"
          : "fixed inset-0 z-50 !m-0 bg-gray-50 dark:bg-gray-900 overflow-auto p-3 space-y-3")
        : (installMode ? "space-y-1 sm:space-y-3" : "space-y-3")}
      data-testid={expandedView ? "frs-expanded-view" : "frs-normal-view"}
    >
      {linkRoom && (
        <div className="flex items-center gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-1.5 text-xs" data-testid="plan-link-banner">
          <span className="font-semibold text-blue-900">From the plan: room {linkRoom}{linkProduct ? ` · ${linkProduct}` : ""}</span>
          <span className="text-blue-700">— your remembered filters come back when you leave</span>
          <button type="button" className="ml-auto text-blue-800 underline" data-testid="plan-link-back"
                  onClick={() => openPlan({ room: linkRoom, product: linkProduct })}>Back to plan</button>
          <button type="button" className="text-blue-800 underline" onClick={dropLink} data-testid="plan-link-clear">Show all</button>
        </div>
      )}
      {/* Filter Bar — on a phone the field view runs edge to edge */}
      <div className={installMode ? "bg-white sm:border sm:border-gray-200 sm:rounded-lg sm:shadow-sm border-y border-gray-200 sm:border-y" : "bg-white border border-gray-200 rounded-lg shadow-sm"}>
        {editMode && !viewing && (
          <FrsEditBar
            projectId={projectId}
            pending={pending}
            postOpen={postOpen}
            onAddRoom={() => setAddRoomOpen(true)}
            onAddProduct={() => setPickerOpen(true)}
            selectedRooms={selected.size ? [...new Set(items.filter((it) => selected.has(it.id))
              .map((it) => [it.building || "", it.floor || "", it.area || "", it.room_number || "", it.room_type || ""].join("||")))].length : 0}
            onBulkSelection={() => {
              const keys = new Set(items.filter((it) => selected.has(it.id))
                .map((it) => [it.building || "", it.floor || "", it.area || "", it.room_number || "", it.room_type || ""].join("||")));
              const rooms = [];
              const seen = new Set();
              for (const it of items) {
                const k = [it.building || "", it.floor || "", it.area || "", it.room_number || "", it.room_type || ""].join("||");
                if (!keys.has(k) || seen.has(k)) continue;
                seen.add(k);
                rooms.push({ key: k, building: it.building || "", floor: it.floor ?? "", area: it.area || "",
                             room_number: it.room_number ?? "", room_name: it.room_name || "", room_type: it.room_type || "" });
              }
              setBulkTarget({ label: `${rooms.length} selected room(s)`, rooms });
            }}
            onClose={leaveEditMode}
            onSaved={() => load()}
          />
        )}
        {viewing && (
          <div className="px-2 py-1.5 flex items-center gap-2 flex-wrap text-xs bg-slate-800 text-white"
               data-testid="frs-version-banner">
            <span className="font-semibold">Viewing {viewing.label} — read only</span>
            <span className="text-slate-300">
              {viewing.mode === "compare"
                ? `${viewData?.diff?.changed ?? 0} changed · ${viewData?.diff?.added ?? 0} added since · ${viewData?.diff?.removed ?? 0} removed since`
                : "changed cells are outlined"}
            </span>
            <button type="button" onClick={() => setViewing(null)} data-testid="frs-version-back"
                    className="ml-auto h-7 px-2 rounded-md bg-white text-slate-900 font-semibold">
              Back to Current
            </button>
          </div>
        )}
        {roomActions && (
          <RoomActionsDialog
            room={roomActions.room}
            roomRef={roomActions.ref}
            roomLabel={roomActions.label}
            products={roomActions.products}
            units={roomActions.units}
            pending={pending}
            isAdmin={String(fieldRole || "").toLowerCase().match(/^(admin|management)$/) !== null}
            onClose={() => setRoomActions(null)}
          />
        )}
        {bulkTarget && (
          <BulkEditDialog
            target={bulkTarget}
            items={items}
            completions={completions}
            pending={pending}
            roomRefOf={roomRefOf}
            onClose={() => setBulkTarget(null)}
          />
        )}
        {pickerOpen && (
          <ProductPickerDialog
            projectId={projectId}
            scopes={editScopes}
            rooms={editRooms}
            pending={pending}
            role={fieldRole}
            actor={currentUser}
            onClose={() => setPickerOpen(false)}
          />
        )}
        <AddRoomScopeWizard
          open={addRoomOpen}
          onOpenChange={setAddRoomOpen}
          projectId={projectId}
          scopes={editScopes}
          fieldOptions={fieldOptions}
          actor={currentUser}
          onStage={(ops, info) => {
            ops.forEach((op) => pending.stage(`new:${op.scope_type}:${op.room_number}`, {
              op,
              room_label: `Room ${op.room_number}${op.room_name ? ` ${op.room_name}` : ""}`,
              label: `new room on ${op.scope_type} with ${op.products.length} product(s)`,
            }));
            toast.success(`${info.count} new room(s) waiting for Save`);
          }}
        />
        {dispatchFilter && dispatchApplied && (
          <div className="px-2 py-1.5 flex items-center gap-2 flex-wrap text-xs bg-blue-50 border-b border-blue-200"
               data-testid="dispatch-filter-chip">
            <Truck className="w-4 h-4 shrink-0 text-blue-700" />
            <span className="text-blue-900 font-semibold">Dispatch filter</span>
            <span className="text-blue-800 truncate">
              {[dispatchFilter.label,
                dispatchFilter.building && `Building ${dispatchFilter.building}`,
                dispatchFilter.floor && `Floor ${dispatchFilter.floor}`,
                dispatchFilter.area && `Area ${dispatchFilter.area}`].filter(Boolean).join(" · ") || "your dispatch"}
            </span>
            <button type="button" onClick={clearDispatch}
                    data-testid="dispatch-filter-clear"
                    className="ml-auto min-h-[36px] px-2 inline-flex items-center gap-1 rounded-md border border-blue-300 bg-white text-blue-800 font-semibold">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
        )}
        {focusIds.size > 0 && (
          <div className="px-3 py-2 flex items-center gap-2 flex-wrap text-sm bg-blue-50 border-b border-blue-200"
               data-testid="frs-focus-banner">
            <Truck className="w-4 h-4 text-blue-700" />
            <span className="text-blue-900 font-medium">
              {focusActive
                ? `Your dispatch${focusLabel ? ` · ${focusLabel}` : ""} — ${items.length} row${items.length === 1 ? "" : "s"}`
                : "Showing the whole job"}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="h-9 ml-auto"
              onClick={() => setFocusOn((v) => !v)}
              data-testid={focusActive ? "frs-show-all-areas-btn" : "frs-back-to-dispatch-btn"}
            >
              {focusActive ? "Show all areas" : "Back to my dispatch"}
            </Button>
          </div>
        )}
        {/* FLD-MOB §5 — the delivery view on a phone gets exactly two rows: what Place / move will
            do (plus Filters and the full-screen toggle on the right), then the action itself with
            the tree controls. Nothing runs off the top of the screen and nothing disappears when
            the matrix goes full screen. */}
        {installMode && delivery && phoneNarrow ? (
          <div className="px-1 py-1 border-b border-gray-200 space-y-1" data-testid="install-compact-bar">
            <div className="flex items-center gap-1 min-w-0">
              <div className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto" data-testid="delivery-toolbar-row">
                {delivery.modes}
              </div>
              <button
                type="button"
                onClick={() => setFieldFiltersOpen((v) => !v)}
                data-testid="install-filters-toggle"
                aria-label="Filters"
                className={`h-8 w-8 shrink-0 grid place-items-center rounded-md border text-sm ${
                  activeChips.length
                    ? "border-blue-300 bg-blue-50 text-blue-800"
                    : "border-gray-300 bg-white text-gray-700"}`}
              >
                {activeChips.length
                  ? <span className="text-xs font-semibold">{activeChips.length}</span>
                  : <SlidersHorizontal className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={() => setExpandedView((v) => !v)}
                data-testid={expandedView ? "frs-collapse-view-btn" : "frs-expand-view-btn"}
                aria-label={expandedView ? "Back to the normal layout" : "Fill the screen with the FRS"}
                className="h-8 w-8 shrink-0 grid place-items-center rounded-md border border-slate-300 bg-transparent text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100"
              >
                {expandedView ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex items-center gap-1.5 min-w-0 overflow-x-auto" data-testid="delivery-toolbar-actions">
              {delivery.actions}
              <div ref={setInstallToolbarSlot}
                   className="flex items-center gap-1.5 text-[11px] text-gray-500 whitespace-nowrap shrink-0"
                   data-testid="install-tree-controls">
                {viewMode === "tree" && renderTreeControls()}
              </div>
            </div>
          </div>
        ) : installMode && (
          <div className={`px-1 py-1 sm:px-2 sm:py-1.5 flex items-center gap-1 sm:gap-2 border-b border-gray-200 flex-wrap ${delivery ? "" : "sm:flex-nowrap"}`}
               data-testid="install-compact-bar">
            <div className={`flex items-center gap-1 flex-wrap min-w-0 ${delivery ? "" : "basis-full sm:basis-auto sm:shrink-0"}`} data-testid="install-material-toggle">
              {/* FLD-MOB §2 — the delivery controls compress onto one scrollable line on a phone
                  instead of wrapping into four rows above the matrix. Desktop is unchanged. */}
              {delivery && (
                <div className="flex sm:contents items-center gap-1 flex-nowrap overflow-x-auto w-full min-w-0"
                     data-testid="delivery-toolbar-row">
                  {delivery.toolbar}
                </div>
              )}
              {!delivery && [["install", "Install"], ["material", "Material"]].map(([k, label]) => (
                <button key={k} type="button" onClick={() => { setMaterialTab(k === "material"); if (k === "material") loadOnSite(); }}
                        data-testid={`install-tab-${k}`}
                        className={`h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm font-semibold rounded-md border ${
                          (materialTab ? "material" : "install") === k
                            ? "bg-gray-900 text-white border-gray-900"
                            : "bg-white text-gray-700 border-gray-300"}`}>
                  {label}
                </button>
              ))}
              {materialTab && !delivery && (
                <span className="ml-1 text-[11px] text-gray-600 whitespace-nowrap" data-testid="install-onsite-total">
                  <span className="font-semibold text-gray-900 tabular-nums">
                    {Object.values(onSite).reduce((s, v) => s + Number(v.on_site || 0), 0)}
                  </span>{" "}
                  on site
                </span>
              )}
              {materialTab && !delivery && (
                <button type="button" onClick={() => setRoomsMove(selectedRooms)} data-testid="install-move-rooms-btn"
                        disabled={selectedRooms.length === 0}
                        title="Tick rooms in the MOVE column — the area row ticks every room under it"
                        className={`ml-0.5 sm:ml-1 h-8 sm:h-9 px-1.5 sm:px-3 text-[11px] sm:text-sm font-semibold rounded-md whitespace-nowrap ${selectedRooms.length ? "bg-emerald-600 text-white" : "bg-white text-gray-500 border border-dashed border-gray-300"}`}>
                  <span className="sm:hidden">{selectedRooms.length ? `Move ${selectedRooms.length}` : "Move"}</span>
                  <span className="hidden sm:inline">
                    {selectedRooms.length ? `Move material in ${selectedRooms.length} room(s)` : "Move material — tick rooms"}
                  </span>
                </button>
              )}
              {materialTab && !(delivery && phoneNarrow) && (
                <button type="button" onClick={() => navigate(`/install/onsite?project=${projectId}`)} data-testid="install-view-onsite-material"
                        title="View all onsite material"
                        className="ml-0.5 sm:ml-1 h-8 sm:h-9 px-1.5 sm:px-3 text-[11px] sm:text-sm font-semibold rounded-md border border-blue-300 bg-blue-50 text-blue-900 whitespace-nowrap">
                  <span className="sm:hidden">All onsite</span>
                  <span className="hidden sm:inline">View All Onsite Material</span>
                </button>
              )}
            </div>
            <div ref={setInstallToolbarSlot}
                 className={`${leanDelivery ? "hidden sm:flex" : "flex"} items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs text-gray-500 whitespace-nowrap overflow-x-auto min-w-0 sm:flex-1
                   basis-auto flex-none max-w-[150px] sm:max-w-none sm:flex-1`}
                 data-testid="install-tree-controls">
              {viewMode === "tree" && renderTreeControls()}
            </div>
              <SearchBar
                testId="install-search"
                placeholder="Search..."
                value={filters.search}
                onChange={(v) => setFilters({ ...filters, search: v })}
                width="flex-1 sm:flex-none sm:w-56 min-w-[104px] sm:min-w-0"
                className={`sm:ml-auto ${delivery ? "hidden" : ""}`}
              />
            <button
              type="button"
              onClick={() => setFieldFiltersOpen((v) => !v)}
              data-testid="install-filters-toggle"
              className={`h-8 sm:h-9 px-2 sm:px-3 shrink-0 ${leanDelivery ? "hidden sm:inline-flex" : "inline-flex"} items-center gap-1 text-sm rounded-md border ${
                activeChips.length
                  ? "border-blue-300 bg-blue-50 text-blue-800"
                  : "border-gray-300 bg-white text-gray-700"}`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              {activeChips.length ? activeChips.length : <span className="hidden sm:inline">Filters</span>}
            </button>
            {/* Full-screen toggle sits beside Filters, not inside the panel. */}
            <button
              type="button"
              onClick={() => setExpandedView((v) => !v)}
              data-testid={expandedView ? "frs-collapse-view-btn" : "frs-expand-view-btn"}
              aria-label={expandedView ? "Back to the normal layout" : "Fill the screen with the FRS"}
              className="h-8 w-8 sm:h-9 sm:w-9 shrink-0 grid place-items-center rounded-md border border-slate-300 bg-transparent text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100"
            >
              {expandedView ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
        )}
        <div className={`items-center gap-2 border-b border-gray-200 max-w-full min-w-0 ${installMode ? "flex-wrap gap-y-1.5 overflow-x-hidden px-1 py-1 sm:flex-nowrap sm:overflow-x-auto sm:px-3 sm:py-1.5" : "relative flex-wrap py-2.5 pl-3 pr-12"} ${installMode && !fieldFiltersOpen ? "hidden" : "flex"}`}>
          {!installMode && <SearchBar
            testId="frs-search"
            placeholder="Search..."
            value={filters.search}
            onChange={(v) => setFilters({ ...filters, search: v })}
          />}
          {/* Button colour = what it does: blue in/out · green write · green outline = mode · neutral outline = move · grey = internal */}
          {!installMode && (
            <>
              <button type="button" onClick={() => setMultiOpen(true)} data-testid="frs-upload-btn"
                      title={opsMode ? "Upload FRS workbook — replaces or merges the master FRS" : "Upload FRS workbook (single or multi-scope)"} className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-medium text-white shadow-sm transition-colors bg-[#007BFF] hover:bg-[#0069d9]">
                <Upload className="w-3.5 h-3.5" /> FRS
              </button>
              {!opsMode && <button type="button" onClick={() => setPunchOpen(true)} data-testid="punch-upload-btn"
                      title="Upload a GC punchlist (XLSX, CSV or text PDF)" className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-medium text-white shadow-sm transition-colors bg-[#007BFF] hover:bg-[#0069d9]">
                <Upload className="w-3.5 h-3.5" /> Punch
              </button>}
              {/* FT-3a — the FRS is edited HERE, in the grid: edits stay pending until ONE Save; the
                  clock half of the split opens the edit revisions. */}
              <span className="inline-flex">
                <button type="button" onClick={() => (editMode ? leaveEditMode() : setEditMode(true))} data-testid="frs-edit-toggle"
                        disabled={!!viewing}
                        title={viewing ? "Back to Current to edit" : editMode ? "Leave edit mode" : "Edit the FRS in this grid"}
                        className={`inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-medium text-white shadow-sm transition-colors rounded-r-none disabled:opacity-50 disabled:cursor-not-allowed ${editMode ? "bg-emerald-600 hover:bg-emerald-700" : "!text-emerald-700 border border-emerald-600 bg-transparent hover:bg-emerald-50 dark:!text-emerald-400"}`}>
                  <Pencil className="w-3.5 h-3.5" /> {editMode ? "Editing" : "FRS"}
                  {pending.count > 0 && (
                    <span className="ml-1 px-1 rounded-full bg-white text-emerald-800 text-[10px] font-bold tabular-nums">{pending.count}</span>
                  )}
                </button>
                <button type="button" data-testid="frs-undo-toggle" disabled={!pending.canUndo}
                        onClick={() => { const u = pending.undo(); if (u) toast.info("Undid last change"); }}
                        title={pending.canUndo ? "Undo the last change" : "Nothing to undo"}
                        className={`inline-flex items-center h-7 px-1.5 rounded-r-md border border-l-0 border-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed ${editMode ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-transparent text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400"}`}>
                  <Undo2 className="w-3.5 h-3.5" />
                </button>
              </span>
              {!opsMode && <button type="button" onClick={() => { setPicking(true); loadMaterial(); }} data-testid="picking-ticket-btn"
                      title="Generate a pull ticket for a location" className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-medium shadow-sm transition-colors bg-slate-200 text-slate-800 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600">
                <ClipboardCheck className="w-3.5 h-3.5" /> Pull
              </button>}
            </>
          )}
          <button
            type="button"
            onClick={async () => {
              if (viewMode === "grid" && gridExportRef.current) { gridExportRef.current(); return; }
              try {
                const r = await api.get(`/projects/${projectId}/frs/export`, { responseType: "blob" });
                const cd = r.headers?.["content-disposition"] || "";
                const m = /filename="?([^"]+)"?/.exec(cd);
                const filename = m ? m[1] : "FRS_export.xlsx";
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url; a.download = filename;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success(`Exported ${filename}`);
              } catch (err) {
                toast.error("Export failed: " + (err?.response?.data?.detail || err.message));
              }
            }}
            data-testid="frs-export-btn"
            title={viewMode === "grid" ? "Download this view exactly as displayed (Excel)" : "Download the full FRS as a Master workbook (INFO tab + one sheet per scope)"}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs font-medium text-white shadow-sm transition-colors bg-[#007BFF] hover:bg-[#0069d9]"
          >
            <Download className="w-3.5 h-3.5" /> FRS
          </button>
          {latestRecon?.reconciliation && (() => {
            const hasIssues = (latestRecon.reconciliation.per_product || []).some(p => p.delta !== null && p.delta !== 0)
              || (latestRecon.reconciliation.row_anomalies || []).length > 0;
            return (
              <button
                onClick={() => setReconOpen(true)}
                data-testid="open-reconciliation-btn"
                title={hasIssues ? "FRS Reconciliation — issues detected" : "FRS Reconciliation — all clean"}
                aria-label="Reconciliation"
                className={`inline-flex items-center justify-center h-8 w-8 border rounded-md ${
                  hasIssues
                    ? "border-red-200 bg-red-50 hover:bg-red-100"
                    : "border-emerald-200 bg-emerald-50 hover:bg-emerald-100"
                }`}
              >
                <ClipboardCheck className={`w-3.5 h-3.5 ${hasIssues ? "text-red-500" : "text-emerald-600"}`} />
              </button>
            );
          })()}
          {project?.frs_status && project?.ops_status && !installMode && (
            <span
              data-testid="frs-version-badge"
              title={project.frs_status === "reconciled" ? "FRS is Current" : "FRS is in Draft"}
              data-status={project.frs_status === "reconciled" ? "current" : "draft"}
              className={`inline-flex h-7 items-center rounded-md border px-2 text-xs font-semibold ${
                project.frs_status === "reconciled" ? "border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
                  : "border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-300"}`}
            >
              FRS
            </span>
          )}
          {installMode && (<>
          <FilterPills testId="frs-filter-pills" pills={[
            { key: "building", label: "Building", options: optionsFor("building").map((b) => ({ value: b, label: formatLevelLabel("building", b) })) },
            { key: "floor", label: "Floor", options: optionsFor("floor").map((f) => ({ value: f, label: formatLevelLabel("floor", f) })) },
            { key: "area", label: "Area", options: optionsFor("area").map((a) => ({ value: a, label: formatLevelLabel("area", a) })) },
            { key: "room", label: "Room", options: optionsFor("room") },
            ...(!installMode ? [{ key: "room_type", label: "Room Type", options: optionsFor("room_type") }] : []),
          ].map((p) => {
            const dim = p.key === "scope" ? "scope_type" : p.key;
            const opts = (p.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
            return { ...p, options: opts, multi: true, bare: true, value: (filters[dim] || []).join("|"),
                     onChange: (v) => setFilterDim(dim, v ? String(v).split("|") : []) };
          }).concat([
            { key: "completed", active: showCompleted,
              label: <span className="inline-flex items-center gap-1" title={showCompleted ? "Completed rooms shown — click to hide" : "Completed rooms hidden — click to show"}><CircleCheck className="h-3 w-3" /> Rooms</span>,
              onClick: () => setShowCompleted((v) => { localStorage.setItem("fieldops-frs-show-completed", v ? "0" : "1"); return !v; }) },
          ])} />
          <button type="button" onClick={resetUndo ? undoReset : resetFilters} data-testid="frs-reset-filters"
                  title={resetUndo ? "Undo — bring back the filters you just reset" : "Reset filters (clears and forgets them for this view)"}
                  className={`grid h-7 w-7 place-items-center rounded-md transition-colors ${resetUndo ? BTN.internal : "border border-slate-300 text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100"}`}>
            {resetUndo ? <Undo2 className="w-3.5 h-3.5" /> : <RotateCcw className="w-3.5 h-3.5" />}
          </button>
          </>)}
          <div className="ml-auto inline-flex items-center gap-2 shrink-0">
          {/* FT-2 §5 — OFCI verification is a foreman task, not the installer's daily need: it
              moved out of the Material toolbar and lives one tap away, in the filter row. */}
          {installMode && materialTab && !delivery && (
            <button type="button" onClick={() => setOfciProject(true)} data-testid="install-verify-ofci-btn"
                    title="Verify OFCI material onsite"
                    className="h-8 px-2 text-xs font-semibold rounded-md border border-emerald-600 bg-transparent text-emerald-700 hover:bg-emerald-50 whitespace-nowrap dark:text-emerald-400">
              Verify OFCI Onsite
            </button>
          )}
          {!installMode && <button
            type="button"
            className="absolute right-3 top-2.5 grid h-7 w-7 place-items-center rounded-md transition-colors border border-slate-300 bg-transparent text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800"
            onClick={() => setExpandedView((v) => !v)}
            data-testid={expandedView ? "frs-collapse-view-btn" : "frs-expand-view-btn"}
            title={expandedView ? "Back to the normal layout (Esc)" : "Fill the screen with the FRS"}
            aria-label={expandedView ? "Collapse" : "Expand"}
          >
            {expandedView ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>}
          </div>
        </div>
        {/* Bulk action row */}
        <div className={`px-3 py-2 items-center gap-2 bg-gray-50/50 border-b border-gray-100 text-sm flex-wrap ${installMode ? "hidden" : "flex"}`}>
          {!opsMode && <>
          <span title={selected.size === 0 ? "Check rooms or a scope in the table below to enable" : ""} className="inline-flex">
          <Button size="sm" disabled={selected.size === 0} onClick={() => openRequestDialog()} className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="request-btn">
            <Send className="w-3.5 h-3.5 mr-1" /> Request
          </Button>
          </span>
          <Button
            size="sm"
            className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={runAutoScan}
            disabled={autoScanning}
            data-testid="auto-request-scan-btn"
            title="Create cards for Ready work whose Start date falls inside the auto-request window (Settings → Labor defaults)"
          >
            <Zap className="w-3.5 h-3.5 mr-1" /> {autoScanning ? "Scanning…" : "Auto Request"}
          </Button>
          {/* Full Job dates — cascade to every building, floor, area and room that has not locked its own value */}
          <span className="inline-flex items-center gap-3 text-xs" data-testid="project-date-bar">
            <span title="Full Job dates — every scope and level inherits them unless locked. Same values the RA list shows."
                  data-testid="full-job-label" className="text-violet-700 dark:text-violet-300"><Lock className="h-3.5 w-3.5" /></span>
            {[["start", "Start"], ["gc", "Completion"]].map(([field, label]) => {
              const spec = { level: "project", scope_type: "" };
              const entry = lockedEntry(spec, field);
              const fromJob = (project?.[field === "start" ? "start_date" : "gc_completion_date"] || "").slice(0, 10);   // synced from the job; a pick here overrides
              return (
                <span key={field} className="inline-flex items-center gap-1" data-testid={`project-date-${field}`}
                      data-locked={entry ? "1" : "0"} data-source={entry?.origin === "project" || (!entry && fromJob) ? "job" : entry ? "override" : "none"}>
                  <span className="text-gray-500">{label}</span>
                  <FrsDateCell plain value={entry?.value || fromJob} onCommit={(v) => saveLevelDate(spec, field, v)} locked={!!entry}
                               hint={entry?.origin === "project" || (!entry && fromJob) ? "Synced from the job's dates — pick a date here to override (it updates the job)" : entry ? "Locked on the job — unlock to clear" : "Not set on the job — pick a date to lock it"}
                               testId={`project-date-input-${field}`} />
                </span>
              );
            })}
          </span>
          </>}
          {selected.size > 0 && (
            <span className="text-xs text-blue-700 font-medium" data-testid="request-selected-count">
              {selected.size} row{selected.size === 1 ? "" : "s"} selected
            </span>
          )}
          <div className="ml-auto inline-flex items-center border border-gray-300 rounded-md overflow-hidden bg-white" role="group" aria-label="View mode">
            <button
              type="button"
              data-testid="view-toggle-tree"
              onClick={() => setViewMode("tree")}
              className={`inline-flex items-center gap-1 px-2.5 h-7 text-xs ${viewMode === "tree" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}
              title="Tree view"
            >
              <List className="w-3.5 h-3.5" /> Tree
            </button>
            <button
              type="button"
              data-testid="view-toggle-grid"
              onClick={() => setViewMode("grid")}
              className={`inline-flex items-center gap-1 px-2.5 h-7 text-xs border-l border-gray-300 ${viewMode === "grid" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}
              title="Grid view (FRS spreadsheet style)"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Grid
            </button>
            <button
              type="button"
              data-testid="view-toggle-punch"
              onClick={() => setViewMode("punch")}
              className={`inline-flex items-center gap-1 px-2.5 h-7 text-xs border-l border-gray-300 ${viewMode === "punch" ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}
              title="Punch view (GC punchlist items)"
            >
              <ClipboardList className="w-3.5 h-3.5" /> Punch
              {punchItems.filter((p) => p.status === "open").length > 0 && (
                <span className="ml-0.5 text-[9px] font-bold bg-amber-200 text-amber-900 rounded-full px-1">
                  {punchItems.filter((p) => p.status === "open").length}
                </span>
              )}
            </button>
          </div>
        </div>
        {/* Filters row — below Request / Auto Request (user 2026-10-22) */}
        {!installMode && (
          <div className="flex items-center gap-2 flex-wrap px-3 py-1.5 border-b border-gray-200 text-sm" data-testid="frs-filter-row">
          <FilterPills testId="frs-filter-pills" pills={[
            { key: "building", label: "Building", options: optionsFor("building").map((b) => ({ value: b, label: formatLevelLabel("building", b) })) },
            { key: "floor", label: "Floor", options: optionsFor("floor").map((f) => ({ value: f, label: formatLevelLabel("floor", f) })) },
            { key: "area", label: "Area", options: optionsFor("area").map((a) => ({ value: a, label: formatLevelLabel("area", a) })) },
            { key: "room", label: "Room", options: optionsFor("room") },
            ...(!installMode ? [{ key: "room_type", label: "Room Type", options: optionsFor("room_type") }] : []),
          ].map((p) => {
            const dim = p.key === "scope" ? "scope_type" : p.key;
            const opts = (p.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
            return { ...p, options: opts, multi: true, bare: true, value: (filters[dim] || []).join("|"),
                     onChange: (v) => setFilterDim(dim, v ? String(v).split("|") : []) };
          }).concat([
            { key: "completed", active: showCompleted,
              label: <span className="inline-flex items-center gap-1" title={showCompleted ? "Completed rooms shown — click to hide" : "Completed rooms hidden — click to show"}><CircleCheck className="h-3 w-3" /> Rooms</span>,
              onClick: () => setShowCompleted((v) => { localStorage.setItem("fieldops-frs-show-completed", v ? "0" : "1"); return !v; }) },
          ])} />
          <button type="button" onClick={resetUndo ? undoReset : resetFilters} data-testid="frs-reset-filters"
                  title={resetUndo ? "Undo — bring back the filters you just reset" : "Reset filters (clears and forgets them for this view)"}
                  className={`grid h-7 w-7 place-items-center rounded-md transition-colors ${resetUndo ? BTN.internal : "border border-slate-300 text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100"}`}>
            {resetUndo ? <Undo2 className="w-3.5 h-3.5" /> : <RotateCcw className="w-3.5 h-3.5" />}
          </button>
          </div>
        )}
        {/* Tree expand-level status strip (mirrors Grid view) — in the field view it lives in the compact bar */}
        {viewMode === "tree" && !installMode && (
          <div className="flex items-center gap-3 px-3 py-1.5 text-xs text-gray-500 border-b border-gray-200 bg-gray-50/40 whitespace-nowrap overflow-x-auto">
            {renderTreeControls()}
          </div>
        )}
        {/* Submit Mobilization Request dialog (1-step) */}
        <Dialog open={reqOpen} onOpenChange={setReqOpen}>
          <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <span>Submit Mobilization Request</span>
                {(() => {
                  const { title: smartTitle, units: smartUnits } = smartTitleFromItems(scopedItems);
                  if (!scopedItems.length) return null;
                  return (
                    <span
                      className="inline-flex items-center gap-1.5 ml-1 px-2 py-0.5 rounded-md border border-blue-200 bg-blue-50 text-blue-800"
                      data-testid="mob-request-smart-title"
                      aria-label={smartUnits ? `${smartTitle} · ${smartUnits}` : smartTitle}
                    >
                      <span className="text-[12px] font-mono font-semibold tracking-tight">{smartTitle}</span>
                      {smartUnits && (
                        <span className="text-[10px] font-normal text-blue-700/80 border-l border-blue-300 pl-1.5 ml-0.5">{smartUnits}</span>
                      )}
                    </span>
                  );
                })()}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-3">
              {selectedScopes.length > 1 && (
                <div className="col-span-3 flex items-center gap-2 flex-wrap text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="request-scope-steps">
                  <span className="text-amber-800 font-medium">One card per scope — do them one at a time:</span>
                  {selectedScopes.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setReqScope(s);
                        setMobForm((f) => ({
                          ...f,
                          title: smartTitleFromItems(selectedItems.filter((i) => (i.scope_type || "") === s)).title,
                        }));
                      }}
                      data-testid={`request-scope-step-${s}`}
                      className={`px-2 py-0.5 rounded-full border font-mono ${s === reqScope
                        ? "bg-blue-600 text-white border-blue-700"
                        : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"}`}
                    >
                      {s} ({selectedItems.filter((i) => (i.scope_type || "") === s).length})
                    </button>
                  ))}
                </div>
              )}
              <div className="col-span-3">
                <div className="flex items-center justify-between mb-1">
                  <Label>Title *</Label>
                  {(() => {
                    const { units: smartUnits } = smartTitleFromItems(scopedItems);
                    if (!smartUnits) return null;
                    return (
                      <span
                        className="text-[11px] font-mono font-semibold text-blue-800 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5"
                        data-testid="mob-request-units"
                        title="Total rooms and units in this selection"
                      >
                        {smartUnits}
                      </span>
                    );
                  })()}
                </div>
                <Input data-testid="mob-title-input" value={mobForm.title} onChange={(e) => setMobForm({ ...mobForm, title: e.target.value })} placeholder="e.g., TRA-BA-F2" />
              </div>
              {/* Row: Install Schedule (2 cols) + Installers Required (1 col) */}
              <div className="col-span-2">
                <Label>Install Schedule</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="mob-date-range-trigger"
                      className="w-full h-9 px-3 text-sm text-left border border-gray-300 rounded-md bg-white hover:bg-gray-50 inline-flex items-center gap-2"
                    >
                      <CalendarDays className="w-3.5 h-3.5 text-gray-500" />
                      {mobForm.requested_start_date && mobForm.requested_end_date ? (
                        <span className="text-gray-900">
                          {fmtDateLabel(mobForm.requested_start_date)} → {fmtDateLabel(mobForm.requested_end_date)}
                          <span className="ml-2 text-xs text-gray-500">
                            ({workdaysBetween(mobForm.requested_start_date, mobForm.requested_end_date)} workday
                            {workdaysBetween(mobForm.requested_start_date, mobForm.requested_end_date) === 1 ? "" : "s"})
                          </span>
                        </span>
                      ) : mobForm.requested_start_date ? (
                        <span className="text-gray-900">{fmtDateLabel(mobForm.requested_start_date)} → <span className="text-gray-400">pick completion</span></span>
                      ) : (
                        <span className="text-gray-400">Pick start and completion dates…</span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      numberOfMonths={2}
                      selected={{
                        from: mobForm.requested_start_date ? new Date(mobForm.requested_start_date + "T00:00:00") : undefined,
                        to: mobForm.requested_end_date ? new Date(mobForm.requested_end_date + "T00:00:00") : undefined,
                      }}
                      onSelect={(range) => {
                        const fmt = (d) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "";
                        const fromIso = fmt(range?.from);
                        const toIso = fmt(range?.to);
                        const n = Math.max(1, Number(mobForm.num_installers) || 1);
                        const days = workdaysBetween(fromIso, toIso);
                        const auto = days > 0 ? days * n * 10 : Number(mobForm.estimated_duration_hours) || 0;
                        setMobForm({
                          ...mobForm,
                          requested_start_date: fromIso,
                          requested_end_date: toIso,
                          estimated_duration_hours: auto || mobForm.estimated_duration_hours,
                          _duration_auto: days > 0,
                        });
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Installers Required</Label>
                <div className="h-9 mt-1.5 flex items-center text-sm text-gray-600" data-testid="mob-installers-auto">
                  auto · 2 or more
                </div>
              </div>
              {/* Row: Est. Duration | Priority | Punchlist — all three
                  align top with same Label height so inputs sit on the
                  same horizontal baseline. */}
              <div className="flex flex-col">
                <Label className="flex items-center gap-1.5 mb-1.5">
                  Est. Duration (hrs)
                  {mobForm._duration_auto && (
                    <span className="text-[9px] uppercase font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-1 py-px rounded" title="Auto = workdays × installers × 10">
                      auto
                    </span>
                  )}
                </Label>
                <Input
                  data-testid="mob-duration-input"
                  type="number"
                  step="0.5"
                  value={mobForm.estimated_duration_hours}
                  onChange={(e) => setMobForm({ ...mobForm, estimated_duration_hours: e.target.value, _duration_auto: false })}
                />
              </div>
              <div className="flex flex-col">
                <Label className="mb-1.5">Priority</Label>
                <select data-testid="mob-priority-select" className="h-9 w-full text-sm border border-gray-300 rounded-md px-2 bg-white" value={mobForm.priority} onChange={(e) => setMobForm({ ...mobForm, priority: e.target.value })}>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="flex flex-col">
                {/* Punch work is flagged server-side from the scope, so this is
                    a read-only indicator, not a control. */}
                <Label className="invisible mb-1.5" aria-hidden="true">.</Label>
                <div className="flex items-center h-9">
                  {String(reqScope || scopedItems[0]?.scope_type || "").toUpperCase() === "PUNCH" ? (
                    <span
                      data-testid="mob-punchlist-indicator"
                      className="text-[10px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 border text-orange-700 bg-orange-50 border-orange-300"
                    >
                      Punchlist
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="col-span-3 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-3 py-2" data-testid="request-installer-note">
                Installers are assigned at dispatch. Each scope gets its own Schedule Pool card, sized automatically
                (2 installers by default, more if the GC completion date is earlier than the calculated ABS completion).
              </div>
              <div className="col-span-3">
                <Label>Requirements</Label>
                <div className="flex items-center gap-4 flex-wrap text-sm h-9 mt-1.5">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" data-testid="mob-badged-toggle" checked={!!mobForm.badged_required} onChange={(e) => setMobForm({ ...mobForm, badged_required: e.target.checked })} />
                    Badged
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" data-testid="mob-drug-toggle" checked={!!mobForm.drug_test_required} onChange={(e) => setMobForm({ ...mobForm, drug_test_required: e.target.checked })} />
                    Drug test
                  </label>
                </div>
              </div>
              <div className="col-span-3">
                {/* Lineout — same structure as the schedule/dispatch popover.
                    Notes saved here are immediately visible in the popover
                    (and vice versa) because both views read/write the same
                    `/api/notes` collection keyed by scope_type + key. */}
                {(() => {
                  const reqScopeType = reqScope || (scopedItems[0]?.scope_type) || "";
                  // 1st-mob detection: no existing mobilization on this
                  // project shares the requested scope. When true, the
                  // Submit button is gated until the four required note
                  // fields each have at least one entry.
                  const isFirstMob = !projectMobs.some((m) => (m.scope_type || "") === reqScopeType);
                  // key → label/level. Level matters: project-level notes are
                  // saved with scope_type="" while scope-level notes use the
                  // requested scope — the lookup must match how each is saved.
                  // Only Parking and Site Logistics requires a note before the
                  // first mobilization of a scope can be submitted (per user).
                  const requiredFields = REQUIRED_LINEOUT_FIELDS;
                  const hasNote = (f) => projectNotes.some((n) =>
                    (n.key || "") === f.key &&
                    (n.scope_type || "") === (f.level === "project" ? "" : reqScopeType) &&
                    (n.text || "").trim());
                  // A typed-but-unsaved draft counts too — it gets auto-saved
                  // when the foreman clicks Submit Request.
                  const hasDraft = (f) => (lineoutDrafts[f.key] || "").trim().length > 0;
                  const missingKeys = requiredFields.filter((f) => !hasNote(f) && !hasDraft(f)).map((f) => f.label);
                  if (mobForm._missingKeys?.join("|") !== missingKeys.join("|") || mobForm._isFirstMob !== isFirstMob) {
                    // store on mobForm so submitRequest can read it without a new closure
                    setTimeout(() => setMobForm((f) => ({ ...f, _missingKeys: missingKeys, _isFirstMob: isFirstMob })), 0);
                  }
                  // Materials inputs derived from the user's selected FRS rows.
                  const totalRooms = new Set(scopedItems.map((i) => `${i.building}|${i.floor}|${i.room_number}`)).size;
                  const summaryParts = {};
                  for (const i of scopedItems) {
                    const { part: iPart, short: iShort } = headerLabels(i);
                    const desc = [iPart, iShort].filter(Boolean).join(" · ");
                    summaryParts[desc] = (summaryParts[desc] || 0) + (i.quantity || 0);
                  }
                  const materialsSummary = Object.entries(summaryParts)
                    .map(([d, q]) => `${q} ${d}`)
                    .join(" · ");
                  const totalUnits = scopedItems.reduce((s, i) => s + (i.quantity || 0), 0);
                  const totalHours = Math.max(1, Number(mobForm.estimated_duration_hours) || 0);
                  // Daily Target default = total units ÷ scheduled workdays
                  // (from the Install Schedule dates). Falls back to the
                  // hours-based estimate until dates are picked.
                  const schedDays = workdaysBetween(mobForm.requested_start_date, mobForm.requested_end_date);
                  const dailyTargetCalc = totalUnits > 0
                    ? (schedDays > 0
                        ? Math.max(1, Math.ceil(totalUnits / schedDays))
                        : Math.max(1, Math.round((totalUnits / totalHours) * 10)))
                    : null;
                  const dailyTargetTip = schedDays > 0
                    ? `Auto: ${totalUnits} units ÷ ${schedDays} workday${schedDays === 1 ? "" : "s"} = ${dailyTargetCalc} units / day`
                    : `Auto: ${totalUnits} units ÷ ${totalHours}h × 10 = ${dailyTargetCalc} units / day (pick install dates to use units ÷ days)`;
                  const onNoteAdded = (note) => setProjectNotes((cur) => [note, ...cur]);
                  const onNoteEdited = (note) => setProjectNotes((cur) => cur.map((n) => n.id === note.id ? note : n));
                  const fieldsBelow = LINEOUT_FIELDS.filter((f) => f.placement !== "after_materials");
                  return (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label>
                          Lineout {reqScopeType ? <span className="ml-1 text-gray-400 font-normal text-xs">· {reqScopeType}</span> : null}
                        </Label>
                        {isFirstMob && (
                          <span
                            className={`text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${missingKeys.length === 0 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-300"}`}
                            data-testid="mob-lineout-validation-pill"
                          >
                            {missingKeys.length === 0 ? "Lineout complete" : `1st mob — ${missingKeys.length} note${missingKeys.length === 1 ? "" : "s"} required`}
                          </span>
                        )}
                      </div>
                      <div className="border border-gray-200 rounded-md bg-white p-2 space-y-2">
                        {dailyTargetCalc != null && (
                          <DailyTargetField
                            projectId={projectId}
                            mobScopeType={reqScopeType}
                            notes={projectNotes}
                            currentUser=""
                            computedValue={dailyTargetCalc}
                            totalUnits={totalUnits}
                            totalHours={totalHours}
                            mathTip={dailyTargetTip}
                            onAdded={onNoteAdded}
                            onEdited={onNoteEdited}
                          />
                        )}
                        <div className="border border-gray-100 rounded">
                          <MaterialsSubSection
                            materialsSummary={materialsSummary}
                            totalRooms={totalRooms}
                            frsItems={scopedItems}
                            openOverride={undefined}
                          />
                        </div>
                        {fieldsBelow.map((f) => (
                          <NoteHistoryField
                            key={f.key}
                            projectId={projectId}
                            mobScopeType={reqScopeType}
                            fieldKey={f.key}
                            label={f.label}
                            level={f.level}
                            autoFill={""}
                            notes={projectNotes}
                            currentUser=""
                            onAdded={onNoteAdded}
                            onEdited={onNoteEdited}
                            onDraftChange={(k, t) => setLineoutDrafts((d) => ({ ...d, [k]: t }))}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            {mobForm._isFirstMob && (mobForm._missingKeys || []).length > 0 && (
              <div data-testid="mob-first-mob-alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800">
                <span className="font-semibold">1st mobilization for this scope — lineout notes required before submitting.</span>{" "}
                Add at least one note under: <span className="font-medium">{(mobForm._missingKeys || []).join(", ")}</span>.
                <span className="block text-[11px] text-red-600/80 mt-0.5">
                  Just type the note — it counts as soon as you type and saves automatically when you submit. These notes carry forward to every future mob request for this scope.
                </span>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setReqOpen(false)}>Cancel</Button>
              <Button
                onClick={submitRequest}
                disabled={mobForm._isFirstMob && (mobForm._missingKeys || []).length > 0}
                className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="mob-submit-btn"
                title={mobForm._isFirstMob && (mobForm._missingKeys || []).length > 0
                  ? `1st mob requires Lineout notes for: ${(mobForm._missingKeys || []).join(", ")}`
                  : "Submit request"}
              >
                Submit Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* FLD-1 — picking mode bar: the selection summary is the source of truth. */}
        {picking && (
          <div className="flex items-center gap-3 flex-wrap px-3 py-2 mb-2 rounded-lg border border-blue-200 bg-blue-50 text-xs"
               data-testid="picking-mode-bar">
            <span className="font-semibold text-blue-900 uppercase tracking-wide text-[10px]">Pulling</span>
            <span className="text-blue-900" data-testid="picking-selection-summary">
              <strong>{new Set(pickSelection.map((s) => s.room_key)).size}</strong> room(s) ·{" "}
              <strong>{new Set(pickSelection.map((s) => s.product_uid)).size}</strong> product(s) ·{" "}
              <strong>{pickSelection.reduce((a, s) => a + s.qty, 0)}</strong> unit(s)
            </span>
            <span className="flex items-center gap-2 text-[10px] text-blue-900"
                  data-testid="picking-legend">
              {[["var(--cell-warn-bg)", "requested"], ["var(--cell-ok-bg)", "on site"], ["var(--cell-bad-bg)", "not on hand"]]
                .map(([tone, label]) => (
                  <span key={label} className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded border border-black/10"
                          style={{ background: tone }} />
                    {label}
                  </span>
                ))}
              <span className="flex items-center gap-1"
                    title="Requested, but the warehouse could not provide it — chase the ticket, do not re-request">
                <span className="w-3 h-3 rounded border border-black/10 relative"
                      style={{ background: "var(--cell-bad-bg)" }}>
                  <span className="absolute top-0 right-0 w-0 h-0 border-t-[6px] border-l-[6px] border-t-amber-500 border-l-transparent" />
                </span>
                requested but short
              </span>
            </span>
            <button
              data-testid="picking-clear-all"
              onClick={() => setPickSel({})}
              disabled={pickSelection.length === 0}
              className="ml-auto h-7 px-3 rounded-md border border-blue-300 bg-white text-blue-800 font-semibold disabled:opacity-50"
            >
              Clear all
            </button>
            <button
              data-testid="picking-select-all"
              onClick={() => togglePickRows(items.filter((it) => !pickSel[it.frs_item_key]))}
              title="Selects every row the filters above are showing"
              className="h-7 px-3 rounded-md border border-blue-300 bg-white text-blue-800 font-semibold"
            >
              Select all shown
            </button>
            <button
              data-testid="picking-request-btn"
              disabled={pickSelection.length === 0}
              onClick={() => setRequestOpen({ selection: pickSelection, note: "" })}
              className="h-7 px-3 rounded-md bg-blue-600 text-white font-semibold disabled:opacity-50"
            >
              Request material ({pickSelection.length})
            </button>
            <button data-testid="picking-tickets-btn" onClick={() => setPickingOpen(true)}
                    className="h-7 px-2 rounded-md border border-blue-300 bg-white text-blue-800">
              Tickets
            </button>
            <button data-testid="picking-exit" onClick={() => { setPicking(false); setPickSel({}); }}
                    className="h-7 px-2 rounded-md border border-gray-300 bg-white text-gray-700">
              Leave pulling
            </button>
          </div>
        )}
        {scheduleDialogs}
        {requestOpen && (
          <PickingRequestDialog
            projectId={projectId}
            selection={requestOpen.selection}
            uncoveredNote={requestOpen.note}
            needBy=""
            actor={currentUser || ""}
            onClose={() => setRequestOpen(null)}
            onCreated={() => { setPickSel({}); loadMaterial(); load(); }}
          />
        )}
        {/* Tree (flattened) OR Grid view */}
        {viewMode === "punch" ? (
          <>
            {punchRoom && (
              <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200" data-testid="punch-room-banner">
                <span className="font-semibold">Punch for room {punchRoom.room_number || punchRoom.room_name}</span>
                <button type="button" className="ml-auto underline" onClick={() => setPunchRoom(null)} data-testid="punch-room-clear">Show all</button>
              </div>
            )}
            <PunchView items={punchRoom ? punchItems.filter((p) => {
              const at = (!p.room_number && allItems.find((it) => it.id === p.frs_item_id)) || p;
              return [at.building || "", at.floor || "", at.area || "", at.room_number || "", at.room_type || ""].join("||") === punchRoom.key;
            }) : punchItems} onChanged={() => { loadPunch(); load(); }} />
          </>
        ) : viewMode === "grid" ? (
          <FrsGridView
                      pendingItems={pendingItems}
            items={liveItems}
            emptyState={emptyState}
            toolbarSlot={installMode ? installToolbarSlot : null}
            selectableIds={delivery?.selectable || null}
            qtyLabel={delivery ? "Released" : ""}
            deliveryMap={delivery?.map || null}
            photoCounts={photoCounts}
            onLocationPhotos={placementPhotosOn ? (ctx) => setMaterialPhotosFor(ctx) : null}
            onInstallPhotos={(ctx) => setInstallPhotosFor(ctx)}
            selected={selected}
            setSelected={setSelected}
            onSetReadiness={(ids, makeReady, label) => confirmReadiness(ids, makeReady, label)}
            renderDateCells={opsMode ? null : renderDateCells}
            hideUnassigned={opsMode}
            onUnassignedChanged={load}
            renderGroupDateCells={installMode || picking || opsMode ? null : ({ level, rooms }) => {
              const first = rooms[0] || {};
              const sameB = (i) => String(i.building || "") === String(first.building || "");
              const sameF = (i) => String(i.floor ?? "") === String(first.floor ?? "");
              const sameA = (i) => String(i.area || "") === String(first.area || "");
              const its = items.filter((i) => sameB(i)
                && (level === "building" || sameF(i))
                && (level !== "area" || sameA(i)));
              const scopes = [...new Set(its.map((i) => i.scope_type || ""))];
              return renderLevelDateCells({
                level,
                scope_type: scopes.length === 1 ? scopes[0] : "",
                building: first.building || "",
                floor: level === "building" ? "" : String(first.floor ?? ""),
                area: level === "area" ? String(first.area || "") : "",
              }, its);
            }}
            completions={completions}
            installMode={installMode}
            expandSignal={gridExpandSignal}
            collapsedInit={gridCollapsed} onCollapsedChange={setGridCollapsed}
            punchCounts={punchCounts}
            hoursByRoom={installMode ? null : hoursByRoom}
            progressMode={progressMode}
            onProgressMode={null}
            onPunchRoom={(r) => { setPunchRoom(r); setViewMode("punch"); }}
            onSetTint={async (col, tint) => {
              try {
                await api.post(`/projects/${projectId}/product-tint`, { product_uid: col.uid || "", scope_type: col.scope || "",
                                                                       product_code: col.code || "", product_description: col.desc || "", tint });
                toast.success(tint ? `Column colour: ${tint === "none" ? "none" : tint}` : "Column colour back to Auto");
                load();
              } catch (e) { toast.error(e?.response?.data?.detail || "Could not set the colour"); }
            }}
            roomColumns={roomColumns}
            headerConfig={headerConfig}
            pickingMode={picking}
            planLinks={planLinks}
            shopLinks={shopLinks} projectId={projectId} linkFrom={viewKey}
            onOpenPlan={planLinks?.hasSet ? openPlan : null}
            dateLabels={opsMode ? [] : picking ? ["Start", "Need Material By"] : onlyStart ? ["Start"] : ["Start", "Completion"]}
            datesCollapsed={onlyStart}
            onToggleDates={picking || opsMode ? null : toggleDates}
            onHand={onHandByUid}
            changedKeys={viewing ? changedKeys : null}
            scopeOptions={scopeOptions} scopeFilter={filters.scope_type || []} onScopeFilter={(v) => setFilterDim("scope_type", v)}
            groupBy={picking ? "location" : groupBy}
            onGroupBy={picking ? null : (g) => { setGroupBy(g); localStorage.setItem("frs.groupBy", g); }}
            onBulkCell={editMode && !viewing && !installMode && !picking ? (t) => setBulkTarget(t) : null}
            exportRef={gridExportRef}
            onExportView={installMode || picking ? null : async (view) => {
              try {
                const r = await api.post("/frs-grid/export-view", { project_id: projectId, ...view }, { responseType: "blob" });
                const m = /filename="?([^"]+)"?/.exec(r.headers?.["content-disposition"] || "");
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url; a.download = m ? m[1] : "FRS view.xlsx";
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success("Exported this view");
              } catch (err) {
                toast.error("Export failed: " + (err?.response?.data?.detail || err.message));
              }
            }}
            editCell={editMode && !viewing && !installMode && !picking ? (r, c, cellRows, q) => {
              // Never the delta row: the parent is the FRS record the quantity belongs to.
              const row = cellRows.find((x) => !x.is_delta) || cellRows[0];
              if (!row) return null;
              // TKO-1 R1 — a line counted from takeoff marks is read-only here: edit it on the plan
              if (row.quantity_source === "marks") {
                return (
                  <button type="button" onClick={(e) => { e.stopPropagation(); openDrawing(navigate, { project: projectId, sheet: row.plan_sheet || row.floor_plan || "", room: row.room_number, product: row.product_code, from: viewKey, scopes: filters.scope_type || [] }); }}
                          title={`${row.plan_marks ?? q} on plan — quantity follows the takeoff marks; edit on the plan`}
                          className="absolute inset-0 w-full h-full text-[10px] font-semibold text-sky-900 bg-sky-50 flex items-center justify-center gap-0.5"
                          data-testid={`frs-on-plan-${r.key}-${c.key}`}>{q} <span className="font-normal">on plan</span></button>
                );
              }
              const key = `q:${row.id}`;
              const staged = pending.get(key);
              const value = staged ? staged.op.qty : q;
              // the grid shows staged quantities live; compare against the saved one
              q = row._saved_qty ?? q;
              return (
                <QtyEditCell
                  value={value}
                  staged={!!staged}
                  testid={`frs-edit-qty-${r.key}-${c.key}`}
                  onCommit={(next) => {
                    if (next === q) { pending.drop(key); return; }
                    pending.stage(key, {
                      op: { op: "set_quantity", item_id: row.id, qty: next },
                      room_label: roomLabelOf(r),
                      label: `${row.product_short || row.product_code || c.label || "item"} ${q} → ${next}`,
                    });
                  }}
                />
              );
            } : null}
            editMetaCell={editMode && !viewing && !installMode && !picking
              ? (r, field) => {
                  if (!["room_name", "room_number", "room_type"].includes(field)) return null;
                  const key = `r:${r.key}:${field}`;
                  const staged = pending.get(key);
                  const current = r[field] || "";
                  return (
                    <input
                      value={staged ? staged.op.fields[field] : current}
                      data-testid={`frs-edit-${field}-${r.key}`}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.target.select()}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (next === current) { pending.drop(key); return; }
                        pending.stage(key, {
                          op: { op: "set_room", room: roomRefOf(r), fields: { [field]: next } },
                          room_label: roomLabelOf(r),
                          label: `${field.replace("room_", "")} “${current}” → “${next}”`,
                        });
                      }}
                      className={`w-full bg-transparent text-xs px-0.5 rounded ${
                        staged ? "ring-2 ring-inset ring-amber-500 bg-amber-50 font-semibold" : "hover:bg-blue-50"}`}
                    />
                  );
                }
              : null}
            cellState={cellState}
            editRowAction={editMode && !viewing && !installMode && !picking
              ? (r) => (
                  <button type="button" data-testid={`frs-room-actions-${r.key}`}
                          title="Room information, duplicate, remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            const rows = items.filter((it) => [it.building || "", it.floor || "",
                              it.area || "", it.room_number || "", it.room_type || ""].join("||") === r.key);
                            setRoomActions({
                              room: r, ref: roomRefOf(r), label: roomLabelOf(r),
                              products: rows.filter((x) => !x.is_delta).length,
                              units: rows.reduce((s, x) => s + Number(
                                (x.is_delta ? x.delta_qty : x.quantity) || 0), 0),
                            });
                          }}
                          className={`ml-1 align-middle px-1 text-[10px] font-bold rounded border ${
                            pending.has(`del:${r.key}`)
                              ? "border-red-300 bg-red-100 text-red-800"
                              : "border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200"}`}>
                    •••
                  </button>
                )
              : null}
            onRemoveProductJob={editMode && !viewing && !installMode && !picking
              ? (info) => {
                  const key = `rmjob:${info.scope_type}:${info.product_uid}`;
                  if (pending.has(key)) { pending.drop(key); return; }
                  pending.stage(key, {
                    op: { op: "remove_product_job", scope_type: info.scope_type,
                          product_uid: info.product_uid },
                    room_label: "Job",
                    label: `remove ${info.label} from ${info.scope_type} — `
                      + `${info.rooms} room(s), ${info.units} unit(s)`,
                  });
                  toast.info("Removal is waiting for Save — Discard puts it back");
                }
              : null}
            onChaseTicket={(ticketNumber) => {
              // "chase from the grid": open the ticket that came up short
              navigate(`?tab=material&ticket=${encodeURIComponent(ticketNumber)}`);
            }}
            pickSel={pickSel}
            materialMode={installMode && materialTab}
            onSite={onSite}
            onLocationCell={(room, roomItems) => setPlacementRoom({ room, items: roomItems })}
            onPickCells={picking ? togglePickRows : null}
            onPickProduct={picking ? togglePickProduct : null}
            onHeaderConfig={installMode ? null : saveHeaderConfig}
            onEditProduct={installMode ? null : openProductEdit}
            onCellAction={delivery
              ? /* Delivery: a product cell opens the same room window the LOCATION cell does — a
                   clean list of everything this ticket carries for that room. */
                (room, scope, roomItems) => setPlacementRoom({ room, items: roomItems })
              : (room, scope, roomItems) => setCompletionCell({
                  room, scope, items: roomItems.filter((i) => (i.scope_type || "") === scope),
                })}
          />
        ) : (
        <div className={`overflow-x-auto ${opsMode ? "frs-ops-tree" : ""}`}>
          <table className="w-full text-sm">
            {expanded.size > 0 && (
              <thead className="bg-gray-50">
                <tr className="text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-2 py-2 w-8"></th>
                  <th className="text-left px-2 py-2 font-medium" style={{ paddingLeft: `${itemIndentPx + 8}px` }}></th>
                  <th className="text-left px-2 py-2 font-medium w-28">Ready</th>
                  <th className="text-left px-2 py-2 font-medium w-24">Status</th>
                  <th className="text-left px-2 py-2 font-medium w-32">Start</th>
                  <th className="text-left px-2 py-2 font-medium w-32">Completion</th>
                  <th className="text-left px-2 py-2 font-medium">Notes</th>
                </tr>
              </thead>
            )}
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-500">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-12 text-center text-gray-500">
                  {emptyState || <>
                  <FileSpreadsheet className="w-6 h-6 mx-auto text-gray-400 mb-2" strokeWidth={1.5} />
                  No FRS items. Upload an FRS Excel or use the seed data.
                  </>}
                </td></tr>
              ) : rows.map((row) => {
                if (row.kind === "group") {
                  const node = row.node;
                  const meta = LEVEL_META[node.levelType] || LEVEL_META.room;
                  const Icon = meta.icon;
                  const isOpen = expanded.has(node.path);
                  const hasChildren = node.children.length > 0 || node.items.length > 0;
                  const allSel = node.allItemIds.length > 0 && node.allItemIds.every((id) => selected.has(id));
                  const someSel = !allSel && node.allItemIds.some((id) => selected.has(id));
                  const agg = groupAgg(node);
                  const isRoomLevel = node.levelType === "room"
                    || (node.labelChain || []).slice(-1)[0]?.levelType === "room";
                  const showActions = ["scope", "building", "floor", "area", "room"].includes(node.levelType);
                  // Readiness dot: green = ready, red = not ready, amber = mixed.
                  const dotColor = {
                    ready:     "#16a34a",
                    partial:   "#f59e0b",
                    not_ready: "#dc2626",
                  }[agg.readinessState] || "#dc2626";
                  const readyLabel = {
                    ready:     "Ready",
                    partial:   `Partially ready — ${agg.roomsReady} of ${agg.roomsTotal} rooms`,
                    not_ready: "Not Ready",
                  }[agg.readinessState] || "—";
                  return (
                    <tr
                      key={`g:${row.node.path}`}
                      className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${
                        isRoomLevel && roomConflict(allItems.filter((i) => node.allItemIds.includes(i.id)))
                          ? "abs-row-conflict" : meta.bg
                      }`}
                      data-conflict={
                        isRoomLevel && roomConflict(allItems.filter((i) => node.allItemIds.includes(i.id))) ? "1" : "0"
                      }
                      data-testid={`tree-row-${node.path}`}
                      onClick={(e) => {
                        // Clicking anywhere on the group row toggles expand,
                        // EXCEPT when the click came from an interactive
                        // descendant (checkbox / button / pill).
                        const tag = (e.target.tagName || "").toLowerCase();
                        if (tag === "input" || tag === "button" || e.target.closest("button")) return;
                        if (hasChildren) toggleNode(node.path);
                      }}
                    >
                      <td className="px-2 py-1.5 align-middle">
                        <input
                          type="checkbox"
                          ref={(el) => { if (el) el.indeterminate = someSel; }}
                          checked={allSel}
                          onChange={() => toggleSelectAllDescendants(node)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`tree-check-${node.path}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-2" style={{ paddingLeft: `${node.depth * 18}px` }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleNode(node.path); }}
                            className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-200 shrink-0"
                            data-testid={`tree-toggle-${node.path}`}
                          >
                            {hasChildren ? (isOpen ? <ChevronDown className="w-3.5 h-3.5 text-gray-600" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-600" />) : null}
                          </button>
                          <Icon className={`w-3.5 h-3.5 ${meta.text}`} strokeWidth={1.75} />
                          <span className={`text-sm font-semibold ${meta.text}`}>
                            {(() => {
                              const chain = node.labelChain || [{ levelType: node.levelType, key: node.key }];
                              // Drop placeholder "—" segments from folded chains
                              // (blank Area/Floor values) so the row reads clean.
                              const shown = chain.length > 1 ? chain.filter((l) => String(l.key) !== "—") : chain;
                              return (shown.length ? shown : chain)
                                .map((l) => formatLevelLabel(l.levelType, l.key))
                                .filter((lbl, i, arr) => i === 0 || lbl !== arr[i - 1])   // "Area A › Area A" → "Area A"
                                .join(" › ");
                            })()}
                          </span>
                          <span className="text-xs text-gray-600">
                            · {node.roomCount} rooms · {node.totalQty} units · {node.readyCount} of {node.totalQty} ready
                          </span>
                          {(() => {
                            // Cluster 11 — open punch items under this node.
                            const uniq = new Set((node.items || []).map((it) =>
                              [it.building || "", it.floor || "", it.area || "", it.room_number || "", it.room_type || ""].join("||")));
                            const total = [...uniq].reduce((acc, rk) => acc + (punchCounts[rk] || 0), 0);
                            return total > 0 ? (
                              <span
                                className="ml-1 text-[10px] font-bold text-amber-900 bg-amber-200 border border-amber-300 rounded-full px-1.5"
                                title={`${total} open punch item(s)`}
                                data-testid={`tree-punch-badge-${node.path}`}
                              >
                                P{total}
                              </span>
                            ) : null;
                          })()}
                        </div>
                      </td>
                      {/* Readiness dot: click to toggle everything below */}
                      <td className="px-2 py-1.5">
                        {showActions && agg.total > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleGroupReadiness(node, agg.readinessState !== "ready")}
                            data-testid={`tree-readiness-${node.path}`}
                            data-readiness={agg.readinessState}
                            aria-label={readyLabel}
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-200/60 transition"
                            title={`${readyLabel} — click to mark ${agg.readinessState === "ready" ? "Not Ready" : "Ready"}`}
                          >
                            <span
                              className="block w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: dotColor }}
                            />
                          </button>
                        )}
                      </td>
                      {/* Pipeline Status pill — clickable to recall the whole group */}
                      <td className="px-2 py-1.5">
                        {showActions && agg.total > 0 && agg.topStatus !== "none" && (
                          <button
                            type="button"
                            onClick={() => recallGroup(node)}
                            disabled={agg.lockedCount === 0}
                            data-testid={`tree-status-${node.path}`}
                            title={agg.lockedCount > 0 ? `Click to recall ${agg.lockedCount} item${agg.lockedCount === 1 ? "" : "s"} → blank/draft` : ""}
                            className={`inline-flex items-center ${agg.lockedCount > 0 ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
                          >
                            <StatusPill value={agg.topStatus} />
                          </button>
                        )}
                      </td>
                      {/* Cluster 5 date columns — editable on room rows,
                          read-only rollup above that. */}
                      {(() => {
                        const nodeItems = allItems.filter((i) => node.allItemIds.includes(i.id));
                        // A folded "Area B › Room" row is still the Area's row: it keeps the level date
                        // cells (lock, link, depends on) so a one-room area behaves like every other area.
                        const roomOnly = node.levelType === "room";
                        const spec = roomOnly ? null : levelSpecOf(node, nodeItems);
                        return renderDateCells(nodeItems, { editable: roomOnly, levelSpec: spec });
                      })()}
                      {/* Notes — clickable on every group row to attach a
                          path-keyed note via the project notes collection. */}
                      <td className="px-2 py-1.5 align-top">
                        <GroupNoteCell
                          node={node}
                          projectId={projectId}
                          notesByKey={groupNotesByKey}
                          currentUser={currentUser}
                          onSaved={onGroupNoteSaved}
                        />
                      </td>
                    </tr>
                  );
                }
                if (row.kind === "summary") {
                  const node = row.node;
                  const summaryPath = `${node.path}/__summary__`;
                  const isOpen = summaryExpanded.has(summaryPath);
                  const accs = accessorySummary(node);
                  const totalUnits = accs.reduce((s, a) => s + a.q, 0);
                  const typeCount = accs.length;
                  const meta = LEVEL_META[node.levelType] || LEVEL_META.room;
                  return (
                    <React.Fragment key={`s:${node.path}`}>
                      <tr
                        className={`border-t border-gray-100 cursor-pointer hover:opacity-80 ${meta.bg}`}
                        data-testid={`tree-summary-${node.path}`}
                        onClick={() => {
                          const ns = new Set(summaryExpanded);
                          if (ns.has(summaryPath)) ns.delete(summaryPath); else ns.add(summaryPath);
                          setSummaryExpanded(ns);
                        }}
                      >
                        <td className="px-2 py-1.5"></td>
                        <td colSpan={7} className="px-2 py-1.5">
                          <div className="flex items-center gap-2 text-[12px]" style={{ paddingLeft: `${(node.depth + 1) * 18}px` }}>
                            <span className="w-5 h-5 inline-flex items-center justify-center rounded">
                              {isOpen ? <ChevronDown className={`w-3.5 h-3.5 ${meta.text}`} /> : <ChevronRight className={`w-3.5 h-3.5 ${meta.text}`} />}
                            </span>
                            <span className={`text-[10px] uppercase font-semibold ${meta.text} bg-white/70 border border-gray-200 rounded px-1.5 py-0.5`}>
                              {meta.prefix ? meta.prefix.trim() : node.levelType} Summary
                            </span>
                            <span className={`${meta.text}`}>
                              <span className="font-semibold">{totalUnits}</span> unit{totalUnits === 1 ? "" : "s"}
                              <span className="mx-1.5 opacity-40">·</span>
                              <span className="font-semibold">{typeCount}</span> type{typeCount === 1 ? "" : "s"}
                            </span>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className={`border-t border-gray-100 ${meta.bg}`}>
                          <td className="px-2 py-1.5"></td>
                          <td colSpan={7} className="px-2 pb-2">
                            <div style={{ paddingLeft: `${(node.depth + 2) * 18}px` }}>
                              {accs.length === 0 ? (
                                <div className="text-[11px] text-gray-400 italic py-1">No items.</div>
                              ) : (
                                <table className="text-[12px] border border-gray-200 rounded bg-white">
                                  <thead className="bg-gray-50 text-gray-500">
                                    <tr>
                                      <th className="text-right px-2 py-1 font-medium w-16">Qty</th>
                                      <th className="text-left px-2 py-1 font-medium">Description</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {accs.map((a) => (
                                      <tr key={a.d} className="border-t border-gray-100">
                                        <td className="px-2 py-1 text-right font-semibold text-gray-800">{a.q}</td>
                                        <td className="px-2 py-1 text-gray-700">{a.d}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                }
                const i = row.item;
                const locked = isLocked(i);
                const isDelta = !!i.is_delta;
                const dQty = Number(i.delta_qty || 0);
                const deltaCls = isDelta
                  ? (dQty > 0
                      ? "bg-emerald-50 hover:bg-emerald-100/60 border-l-2 border-emerald-500"
                      : "bg-rose-50 hover:bg-rose-100/60 border-l-2 border-rose-500")
                  : "";
                return (
                  <tr
                    key={`i:${i.id}`}
                    data-testid={isDelta ? `frs-delta-row-${i.id}` : undefined}
                    className={`border-t border-gray-100 ${isDelta ? deltaCls : `hover:bg-gray-50 ${selected.has(i.id) ? "bg-blue-50/60" : locked ? "bg-amber-50/30" : "bg-white"}`}`}
                  >
                    <td className="px-2 py-1.5">
                      <span
                        onClick={() => locked && toast.error("This item is locked — click the group's Status pill to recall.")}
                        className={locked ? "inline-block cursor-not-allowed" : ""}
                      >
                        <input
                          type="checkbox"
                          checked={locked || selected.has(i.id)}
                          disabled={locked}
                          onChange={() => toggleSelected(i.id)}
                          title={locked ? "Locked — click the group's Status pill to recall." : ""}
                          className={locked ? "opacity-70" : ""}
                          data-testid={`frs-row-${i.id}`}
                        />
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-gray-700" style={{ paddingLeft: `${itemIndentPx + 8}px` }}>
                      <span className="inline-flex items-center gap-2">
                        {isDelta ? (
                          <>
                            <GitPullRequestArrow
                              className={`w-3.5 h-3.5 ${dQty > 0 ? "text-emerald-600" : "text-rose-600"}`}
                              strokeWidth={2}
                            />
                            <span className={`font-bold tabular-nums ${dQty > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                              {dQty > 0 ? `+${dQty}` : dQty}
                            </span>
                            <span className="text-gray-700 font-medium" title={headerLabels(i).tip}>
                              {headerLabels(i).part}
                            </span>
                            {headerLabels(i).short && (
                              <span className="text-[11px] text-gray-400">{headerLabels(i).short}</span>
                            )}
                            <span
                              className={`text-[10px] uppercase font-semibold px-1.5 py-px rounded ${
                                dQty > 0
                                  ? "bg-emerald-100 text-emerald-800"
                                  : "bg-rose-100 text-rose-800"
                              }`}
                              title={i.delta_reason || "Change order"}
                            >
                              Change Order
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="font-semibold text-gray-900 tabular-nums">{i.quantity}{i.quantity_source === "marks" && <span className="ml-1 text-[9px] font-semibold text-sky-800 bg-sky-50 rounded px-1 align-middle" title={`${i.plan_marks ?? i.quantity} on plan — quantity follows the takeoff marks`} data-testid={`frs-on-plan-view-${i.id}`}>on plan</span>}</span>
                            <span title={headerLabels(i).tip} className="font-medium"
                                  data-testid={`frs-item-label-${i.id}`}>
                              {headerLabels(i).part}
                            </span>
                            {headerLabels(i).short && (
                              <span className="text-[11px] text-gray-400">{headerLabels(i).short}</span>
                            )}
                          </>
                        )}
                      </span>
                    </td>
                    {/* Per-line readiness + pipeline status are intentionally
                        muted (small dots) — line items inherit from their
                        group. The actionable controls live on the group row. */}
                    <td className="px-2 py-1.5 w-28">
                      <span
                        className="inline-block w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: READY_VALUES.includes(i.readiness_status || "not_ready") ? "#86efac" : "#fca5a5" }}
                        title={READY_VALUES.includes(i.readiness_status || "not_ready") ? "Ready" : "Not Ready"}
                      />
                    </td>
                    <td className="px-2 py-1.5 w-24">
                      {locked && (
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" title={`In ${i.mobilization_status}`} />
                      )}
                    </td>
                    {/* Dates live on the room+scope row, not on line items. */}
                    <td className="px-2 py-1.5" />
                    <td className="px-2 py-1.5" />
                    <td className="px-2 py-1.5" />
                    {/* Notes cell — inline-editable. Always renders the
                        "+ note" placeholder when empty so item rows match
                        the group rows visually. */}
                    <td className="px-2 py-1.5 text-xs text-gray-500 align-top">
                      <NoteCell item={i} onSaved={(updated) => {
                        setAllItems((cur) => cur.map((x) => x.id === i.id ? { ...x, room_notes: updated } : x));
                      }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
      {/* Cluster 5 — shift propagation prompt */}
      <RDialog open={!!shiftPrompt} onOpenChange={(o) => { if (!o) setShiftPrompt(null); }}>
        <RDialogContent className="max-w-md" data-testid="date-shift-dialog">
          <RDialogHeader>
            <RDialogTitle>
              Shift other areas by {shiftPrompt?.days > 0 ? "+" : ""}{shiftPrompt?.days} day{Math.abs(shiftPrompt?.days || 0) === 1 ? "" : "s"}?
            </RDialogTitle>
          </RDialogHeader>
          <div className="py-1 space-y-2">
            <p className="text-sm text-gray-600">
              You moved the {shiftPrompt?.field === "gc" ? "GC completion" : "start"} date. Completed areas never move.
            </p>
            <button
              data-testid="shift-area-only"
              onClick={() => runShift("area")}
              className="w-full text-left px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-50 text-sm"
            >
              <span className="font-medium">This area only</span>
              <span className="block text-xs text-gray-500">Nothing else moves</span>
            </button>
            <button
              data-testid="shift-this-scope"
              onClick={() => runShift("scope")}
              className="w-full text-left px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-50 text-sm"
            >
              <span className="font-medium">This scope</span>
              <span className="block text-xs text-gray-500">Every other not-complete area of {shiftPrompt?.origin?.scope_type} shifts the same</span>
            </button>
            <button
              data-testid="shift-whole-project"
              onClick={() => runShift("project")}
              className="w-full text-left px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-50 text-sm"
            >
              <span className="font-medium">Whole job</span>
              <span className="block text-xs text-gray-500">Every not-complete area of every scope shifts the same</span>
            </button>
          </div>
        </RDialogContent>
      </RDialog>

      <PickingTicketDialog
        projectId={projectId}
        open={pickingOpen}
        onOpenChange={setPickingOpen}
        scopes={[...new Set(allItems.map((i) => i.scope_type).filter((x) => x && x !== "PUNCH"))]}
        buildings={[...new Set(allItems.map((i) => i.building).filter(Boolean))]}
        selectedRoomKeys={[...new Set(allItems.filter((i) => selected.has(i.id))
          .map((i) => `${i.building || ""}|${i.floor ?? ""}|${i.room_number || ""}`.toLowerCase()))]}
      />
      {editProduct && (
        <ProductRecordDialog
          projectId={projectId}
          product={editProduct}
          scopes={[...new Set(allItems.map((i) => i.scope_type).filter((x) => x && x !== "PUNCH"))]}
          role={fieldRole}
          actor={currentUser}
          onClose={() => setEditProduct(null)}
          onSaved={() => { load(); loadHeaderConfig(); }}
        />
      )}
      <PunchUploadDialog
        open={punchOpen}
        onOpenChange={setPunchOpen}
        projectId={projectId}
        rooms={allItems.filter((i) => i.scope_type !== "PUNCH")}
        scopes={[...new Set(allItems.map((i) => i.scope_type).filter(Boolean))]}
        onApplied={() => { loadPunch(); load(); loadFacets(); }}
      />
      <FrsMultiUploadDialog
        projectId={projectId}
        open={multiOpen}
        masterWarning={opsMode}
        onOpenChange={setMultiOpen}
        onApplied={() => { load(); loadFacets(); loadLatestRecon(); }}
      />
      {completionCell && (
        <CompletionCard
          open={!!completionCell}
          onOpenChange={(o) => { if (!o) setCompletionCell(null); }}
          projectId={projectId}
          scope={completionCell.scope}
          room={completionCell.room}
          roomColumns={roomColumns}
          shopLinks={shopLinks}
          items={completionCell.items}
          completion={completions[`${completionCell.room.key}||${completionCell.scope}`]}
          presetRemaining={presetFor ? presetFor(`${completionCell.room.key}||${completionCell.scope}`) : null}
          origin={focusLabel && onPlanSaved ? focusLabel.replace(/^tapped on /, "") : ""}
          author={installerName || currentUser}
          readOnly={!!picking || !!delivery}
          onSaved={(saved) => {
            loadCompletions(); load(); loadOnSite();
            if (saved !== null && onPlanSaved) onPlanSaved(`${completionCell.room.key}||${completionCell.scope}`);
          }}
          issues={issues.filter((i) => i.scope_type === completionCell.scope
            && (i.room_number || "") === (completionCell.room.room_number || "")
            && (i.building || "") === (completionCell.room.building || ""))}
          onCreateIssue={installMode
            ? () => { setIssueCell({ room: completionCell.room, scope: completionCell.scope }); setCompletionCell(null); }
            : undefined}
        />
      )}
      {roomsMove && (
        <RoomsMoveDialog projectId={projectId} actor={installerName || currentUser || ""} rooms={roomsMove}
                         viewFilter={{ scope: filters.scope_type, building: filters.building, floor: filters.floor, area: filters.area }}
                         onClose={() => setRoomsMove(null)}
                         onDone={async () => { setSelected(new Set()); await load(); await loadOnSite(); }} />
      )}
      {placementRoom && (        <PlacementCard
          projectId={projectId}
          room={placementRoom.room}
          items={placementRoom.items}
          deliveryRoom={delivery?.roomPanel
            ? { ...delivery.roomPanel,
                keys: new Set(placementRoom.items.map((i) => i.frs_item_key).filter(Boolean)) }
            : null}
          actor={installerName || currentUser || ""}
          role={fieldRole || ""}
          onPhotosChanged={loadPhotoCounts}
          viewFilter={{ scope: filters.scope_type, building: filters.building, floor: filters.floor, area: filters.area }}
          onClose={() => setPlacementRoom(null)}
          onChanged={() => { loadOnSite(); load(); }}
        />
      )}
      {planOverlay && (
        <Dialog open onOpenChange={(v) => { if (!v) setPlanOverlay(null); }}>
          <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] sm:max-w-[96vw] sm:w-[96vw] sm:h-[92vh] p-0 gap-0 overflow-hidden"
                         data-testid="delivery-plan-overlay">
            <DialogHeader className="px-3 py-2 border-b border-gray-200">
              <DialogTitle className="text-base">
                Floor plan{planOverlay.room ? ` · Room ${planOverlay.room}` : ""}{planOverlay.sheet ? ` · ${planOverlay.sheet}` : ""}
              </DialogTitle>
            </DialogHeader>
            <div className="h-[calc(100%-45px)] overflow-auto px-2 pb-2" data-testid="delivery-plan-body">
              <DrawingsTab projectId={projectId} me={currentUser || ""} isField canWrite
                           link={{ sheet: planOverlay.sheet, room: planOverlay.room, product: planOverlay.product, markup: "" }}
                           onOpenFrs={() => setPlanOverlay(null)} />
            </div>
          </DialogContent>
        </Dialog>
      )}
      {materialPhotosFor && (
        <MaterialPhotos projectId={projectId} locationLabel={materialPhotosFor.label}
                        roomKey={materialPhotosFor.room?.key || ""}
                        roomLabel={[materialPhotosFor.room?.room_number, materialPhotosFor.room?.room_name].filter(Boolean).join(" · ")}
                        frsItemKeys={materialPhotosFor.frs_item_keys || []}
                        ticketId={delivery?.ticketId || ""}
                        actor={currentUser || ""} role={fieldRole || ""}
                        onClose={() => setMaterialPhotosFor(null)}
                        onChanged={loadPhotoCounts} />
      )}
      {installPhotosFor && (
        <PhotoGallery
          open testid="install-photos" onClose={() => setInstallPhotosFor(null)}
          title={`Installation photos · ${[installPhotosFor.room?.room_number, installPhotosFor.room?.room_name].filter(Boolean).join(" · ")}`}
          subtitle={`${installPhotosFor.scope} · ${installPhotosFor.completion?.mode === "full" ? "marked 100%" : `partial · ${Math.round((installPhotosFor.completion?.percent_complete || 0) * 100)}%`} · recorded by ${installPhotosFor.completion?.author || "—"}`}
          photos={[...((installPhotosFor.completion?.photos || []).filter((p) => !p.is_deleted))]
            .sort((a, b) => String(b.uploaded_at).localeCompare(String(a.uploaded_at)))}
          metaOf={(p) => [
            [installPhotosFor.room?.room_number, installPhotosFor.room?.room_name].filter(Boolean).join(" · "),
            installPhotosFor.scope,
            installPhotosFor.completion?.mode === "full" ? "100%" : "partial",
          ].filter(Boolean).join(" · ")}
          emptyText="No installation photos on this record"
        />
      )}
      {ofciProject && (
        <OfciProjectDialog
          projectId={projectId}
          actor={installerName || currentUser || ""}
          onClose={() => setOfciProject(false)}
          onDone={() => { loadOnSite(); load(); }}
        />
      )}
      {issueCell && (
        <IssueDialog
          open={!!issueCell}
          onOpenChange={(o) => { if (!o) setIssueCell(null); }}
          projectId={projectId}
          scope={issueCell.scope}
          room={issueCell.room}
          author={installerName || currentUser}
          onCreated={loadIssues}
        />
      )}
      <RDialog open={reconOpen} onOpenChange={setReconOpen}>
        <RDialogContent className="max-w-4xl">
          <RDialogHeader>
            <RDialogTitle>FRS Reconciliation Report</RDialogTitle>
          </RDialogHeader>
          <ReconciliationView
            rec={latestRecon?.reconciliation}
            fileName={latestRecon?.file_name}
            uploadedAt={latestRecon?.uploaded_at}
            appliedAt={latestRecon?.applied_at}
          />
        </RDialogContent>
      </RDialog>
    </div>
  );
}
